import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  createAgendaItem,
  createPack,
  deleteAgendaAttachment,
  deleteAgendaItem,
  deletePack,
  deleteDocument,
  getPack,
  listPackCommittees,
  listPacks,
  listQuestions,
  reorderAgendaItems,
  updateAgendaItem,
  updatePack,
  uploadAgendaAttachment,
} from "../../api/boardbacks.api";
import apiClient from "../../api/apiClient";
import { fetchDocument, isTrustedDocumentUrl } from "../../api/documentDownload";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import FormWithValidation from "../../components/common/FormWithValidation";
import BoardPackItemLinks from "../../components/boardbacks/BoardPackItemLinks";
import BoardDocumentViewer from "../../components/boardbacks/BoardDocumentViewer";
import BoardPackQuestionsPanel from "../../components/boardbacks/BoardPackQuestionsPanel";
import { canEditGovernance, canManageGovernance } from "../../accessControl";
import { useAuth } from "../../useAuth";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import StatusBadge from "../../ui/StatusBadge";
import { colors, spacing, fontSizes } from "../../ui/tokens";

const containerStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 0.55fr) 1fr",
  gap: "16px",
  height: "100%",
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
};

const cardHeaderStyle = {
  padding: "14px 16px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};


const pageStyle = {
  display: "grid",
  gap: "16px",
  height: "100%",
};

const shellCardStyle = {
  ...cardStyle,
  padding: "18px 20px",
  display: "grid",
  gap: "14px",
};

const workspaceTabRailStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const meetingTabRailStyle = {
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 4,
};

const agendaListBodyStyle = {
  padding: "10px 12px 12px",
  display: "grid",
  gap: "8px",
  alignContent: "start",
};

const placeholderBodyStyle = {
  ...cardStyle,
  padding: "28px",
  display: "grid",
  gap: "10px",
  minHeight: "300px",
  alignContent: "start",
};

const detailPanelBodyStyle = {
  padding: 16,
  minHeight: "60vh",
};


const getPackMetadata = (pack) =>
  pack?.metadata || pack?.metadata_json || pack?.metadataJson || pack?.metadataJSON || {};

const getPackMeetingDate = (pack) =>
  pack?.meeting_time || pack?.meeting_date || pack?.meetingDate || getPackMetadata(pack).meeting_time || null;

const getPackMeetingTimestamp = (pack) => {
  const meetingDate = getPackMeetingDate(pack);
  if (!meetingDate) return null;
  const parsed = new Date(meetingDate);
  const timestamp = parsed.getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getPackCategory = (pack) => pack?.category || getPackMetadata(pack).category;

const getPackPublishedAt = (pack) =>
  pack?.published_at || pack?.publishedAt || getPackMetadata(pack).published_at || getPackMetadata(pack).publishedAt || null;

const formatPackDateTime = (value, fallback = "Not set") => {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
};

const imageNamePattern = /\.(png|jpe?g|gif|webp|svg)$/i;
const pdfNamePattern = /\.pdf$/i;

const getDocName = (doc) => doc?.name || doc?.original_filename || doc?.filename || "Document";
const getDocType = (doc) => doc?.type || doc?.mime_type || "";
const getDocSize = (doc) => doc?.size ?? doc?.size_bytes ?? null;

const isPdfDoc = (name, type) => type === "application/pdf" || pdfNamePattern.test(name || "");
const isImageDoc = (name, type) => type.startsWith("image/") || imageNamePattern.test(name || "");

const resolvePreviewUrl = (url) => {
  if (!url || url.startsWith("http")) return url;
  const base = apiClient.defaults.baseURL || "";
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  if (normalizedBase.startsWith("http")) {
    return `${normalizedBase}${url.startsWith("/") ? url : `/${url}`}`;
  }
  return `${window.location.origin}${normalizedBase}${url.startsWith("/") ? url : `/${url}`}`;
};

const readPreviewFailure = async (
  response,
  fallback = "Preview not available. Download or open in your viewer."
) => {
  if (!response) return fallback;
  let detail = "";
  const contentType = response.headers?.get?.("content-type") || "";
  try {
    if (contentType.includes("application/json")) {
      const payload = await response.clone().json();
      detail = payload?.detail || "";
    } else {
      const text = await response.clone().text();
      if (text && !text.trim().startsWith("<")) {
        detail = text.trim();
      }
    }
  } catch (_err) {
    detail = "";
  }
  if (detail) {
    return `Preview unavailable (${response.status}): ${detail}`;
  }
  if (response.status === 403) {
    return "Preview unavailable (403): You no longer have access to this document.";
  }
  if (response.status === 404) {
    return "Preview unavailable (404): The stored file could not be found.";
  }
  return fallback;
};

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return "";
  const thresh = 1024;
  if (bytes < thresh) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let u = -1;
  let val = bytes;
  do {
    val /= thresh;
    ++u;
  } while (val >= thresh && u < units.length - 1);
  return `${val.toFixed(1)} ${units[u]}`;
};

const getQuestionActorName = (question) =>
  question?.author?.username || question?.author?.email || (question?.author_id ? `User #${question.author_id}` : "Unknown user");

const filterQuestionsForAgendaItem = (questions, item, docs = []) => {
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const safeDocs = Array.isArray(docs) ? docs : [];
  const agendaItemId = item?.id ?? item?._localId ?? null;
  const attachmentIds = new Set(
    safeDocs
      .map((doc) => doc?.agenda_attachment_id || doc?.id || null)
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value))
  );
  const documentIds = new Set(
    safeDocs
      .map((doc) => doc?.document_id || (!doc?.agenda_attachment_id ? doc?.id : null))
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value))
  );
  return safeQuestions.filter((question) => {
    if (agendaItemId && String(question?.agenda_item_id || "") === String(agendaItemId)) {
      return true;
    }
    if (question?.agenda_attachment_id && attachmentIds.has(String(question.agenda_attachment_id))) {
      return true;
    }
    if (question?.document_id && documentIds.has(String(question.document_id))) {
      return true;
    }
    return false;
  });
};

const defaultVoting = { enabled: false, question: "", options: ["Approve", "Reject"] };
const defaultLinked = { risks: [], controls: [], actions: [] };

const normalizeAgendaItem = (item, idx = 0) => {
  const metadata = item?.metadata || item?.metadata_json || item?.metadataJson || item?.metadataJSON || {};
  const attachments = Array.isArray(item?.attachments)
    ? item.attachments
    : Array.isArray(item?.documents)
      ? item.documents
      : item?.document
        ? [item.document]
        : [];
  const normalizedAttachments = attachments.map((doc) => ({
    ...doc,
    agenda_attachment_id: doc.agenda_attachment_id ?? doc.id,
    agenda_item_id: doc.agenda_item_id ?? item?.id ?? null,
    kind: doc.kind || "agenda",
  }));
  return {
    ...item,
    _localId: item?.id ?? item?._localId ?? `tmp-${idx}`,
    description: item?.notes ?? item?.description ?? metadata.description ?? "",
    owner: metadata.owner ?? item?.owner ?? "",
    time_alloc: metadata.time_alloc ?? item?.time_alloc ?? "",
    voting: metadata.voting ?? item?.voting ?? defaultVoting,
    linked: metadata.linked ?? item?.linked ?? defaultLinked,
    documents: normalizedAttachments,
  };
};

const getAgendaItemKey = (item, fallback = "") => item?.id ?? item?._localId ?? fallback;

const buildAgendaPayload = (item, orderOverride = null) => {
  const baseMetadata = item?.metadata || item?.metadata_json || item?.metadataJson || {};
  const { linked: _linked, ...agendaMetadata } = baseMetadata;
  return {
    title: item?.title || "",
    notes: item?.description || item?.notes || null,
    order: typeof orderOverride === "number" ? orderOverride : item?.order ?? 0,
    metadata: {
      ...agendaMetadata,
      owner: item?.owner || null,
      time_alloc: item?.time_alloc || null,
      voting: item?.voting || null,
    },
  };
};

export default function BoardBacksList({ initialCreateOpen = false }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user;
  const { data: packs = [], isLoading, refetch } = useQuery({ queryKey: ["boardbacks"], queryFn: listPacks });
  const [workspaceSection, setWorkspaceSection] = useState("meeting-packs");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedPackTab, setSelectedPackTab] = useState("overview");
  const [createOpen, setCreateOpen] = useState(Boolean(initialCreateOpen));
  const [createCommittees, setCreateCommittees] = useState([]);
  const [createCommitteesLoading, setCreateCommitteesLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaSaving, setAgendaSaving] = useState({});
  const [agendaErrors, setAgendaErrors] = useState({});
  const [agendaUploading, setAgendaUploading] = useState({});
  const [agendaLastSavedAt, setAgendaLastSavedAt] = useState(null);
  const [agendaCreating, setAgendaCreating] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [collapsedAgendaItems, setCollapsedAgendaItems] = useState({});
  const [packPreviewOpen, setPackPreviewOpen] = useState(false);
  const [packPreviewTarget, setPackPreviewTarget] = useState(null);
  const [draggingAgendaKey, setDraggingAgendaKey] = useState(null);
  const [dragOverAgendaKey, setDragOverAgendaKey] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [sortOrder, setSortOrder] = useState("recent");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmAgendaDeleteId, setConfirmAgendaDeleteId] = useState(null);
  const [confirmPackDelete, setConfirmPackDelete] = useState(null);
  const [packDeleteError, setPackDeleteError] = useState("");
  const agendaItemsRef = useRef([]);
  const meetingTabRefs = useRef(new Map());
  const agendaDetailRefs = useRef(new Map());
  const governanceCanEdit = canEditGovernance(user);
  const governanceCanManage = canManageGovernance(user);
  const isAdminEquivalent = String(user?.role || "").toUpperCase() === "ADMIN" || Boolean(user?.is_admin_equivalent);
  const agendaSaveTimers = useRef({});
  const {
    data: packDetail,
    isLoading: detailLoading,
    isError: detailError,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["boardback-detail", selectedId],
    queryFn: () => getPack(selectedId),
    enabled: Boolean(selectedId),
  });

  const categoryOptions = useMemo(() => {
    const builtIn = ["Board", "RACC", "People Committee"];
    const fromData = packs
      .map((p) => getPackCategory(p))
      .filter(Boolean)
      .map((c) => c.trim())
      .filter(Boolean);
    return Array.from(new Set([...builtIn, ...fromData]));
  }, [packs]);
  const createCommitteeOptions = useMemo(
    () =>
      (Array.isArray(createCommittees) ? createCommittees : []).map((committee) => ({
        value: String(committee.id),
        label: committee.name,
      })),
    [createCommittees]
  );

  const filteredPacks = useMemo(() => {
    const list = Array.isArray(packs) ? [...packs] : [];
    const filtered = list.filter((p) => {
      const meetingDate = getPackMeetingDate(p);
      const categoryValue = getPackCategory(p);
      const categoryMatch =
        filterCategory === "all" ||
        ((categoryValue || "").toLowerCase() === filterCategory.toLowerCase() && filterCategory.trim() !== "");
      const dateMatch =
        !filterDate ||
        (meetingDate &&
          new Date(meetingDate).toISOString().slice(0, 10) === new Date(filterDate).toISOString().slice(0, 10));
      return categoryMatch && dateMatch;
    });
    filtered.sort((a, b) => {
      const aDate = getPackMeetingDate(a) ? new Date(getPackMeetingDate(a)).getTime() : 0;
      const bDate = getPackMeetingDate(b) ? new Date(getPackMeetingDate(b)).getTime() : 0;
      return sortOrder === "recent" ? bDate - aDate : aDate - bDate;
    });
    return filtered;
  }, [packs, filterCategory, filterDate, sortOrder]);

  const meetingTabPacks = useMemo(() => {
    const dated = [];
    const undated = [];

    filteredPacks.forEach((pack) => {
      const timestamp = getPackMeetingTimestamp(pack);
      if (timestamp === null) {
        undated.push(pack);
        return;
      }
      dated.push({ pack, timestamp });
    });

    dated.sort((a, b) => a.timestamp - b.timestamp);
    undated.sort((a, b) => String(a?.title || "").localeCompare(String(b?.title || "")));

    return [...dated.map((entry) => entry.pack), ...undated];
  }, [filteredPacks]);

  useEffect(() => {
    if (initialCreateOpen) {
      setCreateOpen(true);
      setCreateError("");
    }
  }, [initialCreateOpen]);

  useEffect(() => {
    let cancelled = false;
    if (!createOpen || !governanceCanEdit) {
      setCreateCommittees([]);
      setCreateCommitteesLoading(false);
      return undefined;
    }

    setCreateCommitteesLoading(true);
    listPackCommittees()
      .then((data) => {
        if (cancelled) return;
        setCreateCommittees(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCreateCommittees([]);
        setCreateError((current) => current || err?.response?.data?.detail || err?.message || "Unable to load committees");
      })
      .finally(() => {
        if (!cancelled) {
          setCreateCommitteesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [createOpen, governanceCanEdit]);

  useEffect(() => {
    if (meetingTabPacks.length > 0) {
      setSelectedId((prev) => {
        const stillVisible = prev && meetingTabPacks.find((p) => p.id === prev);
        if (stillVisible) return prev;

        const now = Date.now();
        const upcoming = meetingTabPacks
          .map((pack) => ({ pack, timestamp: getPackMeetingTimestamp(pack) }))
          .filter((entry) => entry.timestamp !== null && entry.timestamp >= now)
          .sort((a, b) => a.timestamp - b.timestamp);
        if (upcoming.length > 0) {
          return upcoming[0].pack.id;
        }

        const past = meetingTabPacks
          .map((pack) => ({ pack, timestamp: getPackMeetingTimestamp(pack) }))
          .filter((entry) => entry.timestamp !== null && entry.timestamp < now)
          .sort((a, b) => b.timestamp - a.timestamp);
        if (past.length > 0) {
          return past[0].pack.id;
        }

        return meetingTabPacks[0].id;
      });
    } else {
      setSelectedId(null);
    }
  }, [meetingTabPacks]);

  useEffect(() => {
    if (workspaceSection !== "meeting-packs" || !selectedId) return;
    const target = meetingTabRefs.current.get(selectedId);
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [meetingTabPacks, selectedId, workspaceSection]);

  useEffect(() => {
    setSelectedPackTab("overview");
  }, [selectedId]);

  const selectedPack = useMemo(() => {
    const fromList = meetingTabPacks.find((p) => p.id === selectedId) || packs.find((p) => p.id === selectedId);
    const detailData = packDetail?.data || packDetail;
    return detailData || fromList || null;
  }, [packs, meetingTabPacks, selectedId, packDetail]);
  const selectedPackDocuments = useMemo(() => {
    const agendaItems = Array.isArray(selectedPack?.agenda_items)
      ? selectedPack.agenda_items
      : Array.isArray(selectedPack?.agendaItems)
        ? selectedPack.agendaItems
        : [];
    return agendaItems.flatMap((item) =>
      Array.isArray(item.documents)
        ? item.documents
        : Array.isArray(item.attachments)
          ? item.attachments
          : item.document
            ? [item.document]
            : []
    );
  }, [selectedPack]);
  const selectedPackAgendaAttachmentCount = selectedPackDocuments.filter(
    (document) => document?.agenda_attachment_id || document?.agenda_item_id
  ).length;
  const packPreviewAgendaItems = useMemo(() => {
    const sourceItems = Array.isArray(selectedPack?.agenda_items)
      ? selectedPack.agenda_items
      : Array.isArray(selectedPack?.agendaItems)
        ? selectedPack.agendaItems
        : [];
    return sourceItems
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((item, idx) => normalizeAgendaItem(item, idx));
  }, [selectedPack]);
  const canMutateAgenda = governanceCanEdit && Boolean(selectedPack?.id);
  const canManageAgenda = governanceCanManage && Boolean(selectedPack?.id);
  const canDeleteDocuments = canManageAgenda && (selectedPack?.status || "").toLowerCase() !== "archived";
  const selectedIsDraft = (selectedPack?.status || "").toLowerCase() === "draft";
  const canDeletePack =
    governanceCanManage &&
    selectedIsDraft &&
    !selectedPack?.published_version_id &&
    !(Array.isArray(selectedPack?.versions) && selectedPack.versions.length > 0);

  useEffect(() => {
    if (!selectedPack && packPreviewOpen) {
      setPackPreviewOpen(false);
    }
  }, [selectedPack, packPreviewOpen]);

  useEffect(() => {
    agendaItemsRef.current = agendaItems;
  }, [agendaItems]);

  useEffect(() => {
    if (!agendaItems.length) {
      if (expandedItemId !== null) {
        setExpandedItemId(null);
      }
      if (Object.keys(collapsedAgendaItems).length > 0) {
        setCollapsedAgendaItems({});
      }
      return;
    }
    const currentExists = agendaItems.some((item) => (item.id ?? item._localId) === expandedItemId);
    if (!currentExists) {
      setExpandedItemId(agendaItems[0].id ?? agendaItems[0]?._localId ?? null);
    }
    setCollapsedAgendaItems((prev) => {
      const validKeys = new Set(agendaItems.map((item, idx) => String(getAgendaItemKey(item, idx))));
      const next = Object.fromEntries(
        Object.entries(prev).filter(([key, value]) => validKeys.has(key) && value)
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [agendaItems, expandedItemId, collapsedAgendaItems]);

  useEffect(() => {
    if (!agendaItems.length) {
      if (draggingAgendaKey !== null) {
        setDraggingAgendaKey(null);
      }
      if (dragOverAgendaKey !== null) {
        setDragOverAgendaKey(null);
      }
      return;
    }

    const currentKeys = new Set(agendaItems.map((item, idx) => String(getAgendaItemKey(item, idx))));
    if (draggingAgendaKey !== null && !currentKeys.has(String(draggingAgendaKey))) {
      setDraggingAgendaKey(null);
    }
    if (dragOverAgendaKey !== null && !currentKeys.has(String(dragOverAgendaKey))) {
      setDragOverAgendaKey(null);
    }
  }, [agendaItems, draggingAgendaKey, dragOverAgendaKey]);

  const queueAgendaSave = useCallback(
    (itemId) => {
      if (!governanceCanEdit) return;
      if (!selectedPack?.id || !itemId) return;
      if (agendaSaveTimers.current[itemId]) {
        clearTimeout(agendaSaveTimers.current[itemId]);
      }
      agendaSaveTimers.current[itemId] = setTimeout(async () => {
        const currentItem = agendaItemsRef.current.find((ag) => ag.id === itemId);
        if (!currentItem) return;
        setAgendaSaving((prev) => ({ ...prev, [itemId]: true }));
        setAgendaErrors((prev) => ({ ...prev, [itemId]: "" }));
        try {
          await updateAgendaItem(selectedPack.id, itemId, buildAgendaPayload(currentItem));
          setAgendaLastSavedAt(new Date());
        } catch (err) {
          setAgendaErrors((prev) => ({
            ...prev,
            [itemId]: err?.response?.data?.detail || err?.message || "Failed to save agenda item",
          }));
        } finally {
          setAgendaSaving((prev) => ({ ...prev, [itemId]: false }));
        }
      }, 500);
    },
    [governanceCanEdit, selectedPack?.id]
  );

  useEffect(() => {
    return () => {
      Object.values(agendaSaveTimers.current).forEach((timer) => clearTimeout(timer));
      agendaSaveTimers.current = {};
    };
  }, [selectedId]);

  const updateAgendaState = useCallback(
    (updater, options = {}) => {
      setAgendaItems((prev) => {
        const next = (typeof updater === "function" ? updater(prev) : updater) || [];
        const persistIds = options.persistIds || [];
        persistIds.forEach((id) => queueAgendaSave(id));
        return next;
      });
    },
    [queueAgendaSave]
  );

  const requestDocDelete = (doc, itemKey) => {
    setConfirmDelete({ doc, itemKey });
  };

  const handleConfirmDelete = async (pendingDelete) => {
    if (!canDeleteDocuments) return;
    if (!pendingDelete) return;
    const { doc, itemKey } = pendingDelete;
    const docName = getDocName(doc);
    const docSize = getDocSize(doc);
    const docKey = doc.id || doc.url || doc.preview || `${docName}-${docSize}`;

    updateAgendaState((prev) =>
      prev.map((ag) => {
        if ((ag.id ?? ag._localId) === itemKey) {
          const nextDocs = (ag.documents || []).filter((d) => {
            const key = d.id || d.url || d.preview || `${getDocName(d)}-${getDocSize(d)}`;
            return key !== docKey;
          });
          return {
            ...ag,
            documents: nextDocs,
          };
        }
        return ag;
      }),
      { persistIds: [] }
    );

    const isAgendaAttachment = doc?.kind === "agenda" || doc?.agenda_attachment_id || doc?.agenda_item_id;
    if (isAgendaAttachment && selectedPack?.id && canDeleteDocuments) {
      try {
        await deleteAgendaAttachment(selectedPack.id, doc.agenda_attachment_id || doc.id);
        setAgendaLastSavedAt(new Date());
      } catch (_err) {
        // Ignore delete errors; UI state already updated.
      }
      return;
    }

    if (doc.id && canDeleteDocuments) {
      try {
        await deleteDocument(doc.id);
        refetchDetail();
      } catch (_err) {
        // Ignore delete errors; UI state already updated.
      }
    }
  };

  useEffect(() => {
    if (selectedPack) {
      const meta = getPackMetadata(selectedPack);
      setEditForm({
        title: selectedPack.title || "",
        description: selectedPack.description || meta.description || "",
        status: selectedPack.status || meta.status || "",
        owner: selectedPack.owner || meta.owner || "",
        meeting_time: getPackMeetingDate(selectedPack) || "",
        meeting_location: meta.meeting_location || "",
        category: getPackCategory(selectedPack) || "",
      });
      const items = Array.isArray(selectedPack.agenda_items)
        ? selectedPack.agenda_items
        : Array.isArray(selectedPack.agendaItems)
          ? selectedPack.agendaItems
          : [];
      const orderedItems = items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const normalized = orderedItems.map((item, idx) => normalizeAgendaItem(item, idx));
      setAgendaItems(normalized);
      setAgendaErrors({});
      setAgendaSaving({});
      setAgendaUploading({});
      setAgendaLastSavedAt(null);
      setExpandedItemId((current) => {
        if (current && normalized.some((it) => (it.id ?? it._localId) === current)) {
          return current;
        }
        return normalized[0]?.id ?? normalized[0]?._localId ?? null;
      });
    } else {
      setEditForm(null);
      setAgendaItems([]);
      setExpandedItemId(null);
      setAgendaErrors({});
      setAgendaSaving({});
      setAgendaUploading({});
      setAgendaLastSavedAt(null);
    }
  }, [selectedPack]);

  const handleSave = async (values) => {
    if (!selectedPack?.id || !values || !governanceCanEdit) return;
    if (!selectedIsDraft) {
      setEditError("Only draft packs can be edited. Unpublish or duplicate to change this pack.");
      return;
    }
    let meetingDateIso = null;
    if (values.meeting_time) {
      const parsed = new Date(values.meeting_time);
      if (Number.isNaN(parsed.getTime())) {
        setEditError("Invalid meeting date/time");
        return;
      }
      meetingDateIso = parsed.toISOString();
    }
    setEditLoading(true);
    setEditError("");
    try {
      await updatePack(selectedPack.id, {
        title: values.title.trim(),
        meeting_date: meetingDateIso,
        metadata: {
          description: values.description || null,
          status: values.status || null,
          owner: values.owner || null,
          meeting_location: values.meeting_location || null,
          category: values.category || null,
          meeting_time: values.meeting_time || null,
        },
      });
      await Promise.all([refetch(), refetchDetail()]);
      setEditing(false);
    } catch (err) {
      setEditError(err?.response?.data?.detail || err?.message || "Failed to update pack");
    } finally {
      setEditLoading(false);
    }
  };

  const closeCreateModal = useCallback(() => {
    setCreateError("");
    setCreateOpen(false);
    if (initialCreateOpen) {
      navigate("/boardbacks", { replace: true });
    }
  }, [initialCreateOpen, navigate]);

  const handleCreatePack = async (values) => {
    if (!values || !governanceCanEdit) return;
    let meetingDateIso = null;
    if (values.meeting_time) {
      const parsed = new Date(values.meeting_time);
      if (Number.isNaN(parsed.getTime())) {
        setCreateError("Invalid meeting date/time");
        return;
      }
      meetingDateIso = parsed.toISOString();
    }

    setCreateLoading(true);
    setCreateError("");
    try {
      const created = await createPack({
        title: values.title.trim(),
        committee_id: Number(values.committee_id),
        meeting_date: meetingDateIso,
        metadata: {
          category: values.category || null,
          meeting_time: values.meeting_time || null,
          meeting_location: values.meeting_location || null,
          description: values.description || null,
        },
      });
      await refetch();
      setSelectedId(created?.id || null);
      closeCreateModal();
    } catch (err) {
      setCreateError(err?.response?.data?.detail || err?.message || "Failed to create pack");
    } finally {
      setCreateLoading(false);
    }
  };

  const persistAgendaOrder = useCallback(
    async (items) => {
      if (!governanceCanEdit) return;
      if (!selectedPack?.id || !Array.isArray(items) || items.length === 0) return;
      try {
        setAgendaSaving((prev) => ({ ...prev, _order: true }));
        const payload = items
          .filter((item) => item.id)
          .map((item, idx) => ({ id: item.id, order: idx }));
        await reorderAgendaItems(selectedPack.id, payload);
        setAgendaLastSavedAt(new Date());
        setAgendaErrors((prev) => ({ ...prev, _order: "" }));
      } catch (err) {
        setAgendaErrors((prev) => ({
          ...prev,
          _order: err?.response?.data?.detail || err?.message || "Failed to reorder agenda items",
        }));
      } finally {
        setAgendaSaving((prev) => ({ ...prev, _order: false }));
      }
    },
    [governanceCanEdit, selectedPack?.id]
  );

  const reorderAgendaList = useCallback(
    (sourceKey, targetKey) => {
      if (!governanceCanEdit) return;
      if (!sourceKey || !targetKey || String(sourceKey) === String(targetKey)) return;

      updateAgendaState((prev) => {
        const sourceIdx = prev.findIndex((item, idx) => String(getAgendaItemKey(item, idx)) === String(sourceKey));
        const targetIdx = prev.findIndex((item, idx) => String(getAgendaItemKey(item, idx)) === String(targetKey));
        if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return prev;

        const next = [...prev];
        const [moved] = next.splice(sourceIdx, 1);
        next.splice(targetIdx, 0, moved);
        const ordered = next.map((item, idx) => ({ ...item, order: idx }));
        persistAgendaOrder(ordered);
        return ordered;
      });
    },
    [governanceCanEdit, persistAgendaOrder, updateAgendaState]
  );

  const handleAddAgendaItem = async () => {
    if (!canMutateAgenda || agendaCreating) return;
    setAgendaCreating(true);
    try {
      const payload = buildAgendaPayload({
        title: "",
        description: "",
        owner: "",
        time_alloc: "",
        voting: defaultVoting,
        linked: defaultLinked,
      }, 0);
      const created = await createAgendaItem(selectedPack.id, payload);
      const normalized = normalizeAgendaItem(created, 0);
      setAgendaItems((prev) => {
        const next = [normalized, ...prev];
        const ordered = next.map((item, idx) => ({ ...item, order: idx }));
        persistAgendaOrder(ordered);
        return ordered;
      });
      setExpandedItemId(normalized.id ?? normalized._localId);
      setAgendaLastSavedAt(new Date());
      setAgendaErrors((prev) => ({ ...prev, _create: "" }));
    } catch (err) {
      setAgendaErrors((prev) => ({
        ...prev,
        _create: err?.response?.data?.detail || err?.message || "Failed to create agenda item",
      }));
    } finally {
      setAgendaCreating(false);
    }
  };

  const handleAgendaUpload = async (itemId, files) => {
    if (!canMutateAgenda || !itemId || !files?.length) return;
    setAgendaUploading((prev) => ({ ...prev, [itemId]: true }));
    try {
      const uploads = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadAgendaAttachment(selectedPack.id, itemId, file);
        uploads.push(uploaded);
      }
      setAgendaItems((prev) =>
        prev.map((ag) =>
          ag.id === itemId
            ? {
                ...ag,
                documents: [
                  ...(ag.documents || []),
                  ...uploads.map((doc) => ({
                    ...doc,
                    agenda_attachment_id: doc.agenda_attachment_id ?? doc.id,
                    agenda_item_id: doc.agenda_item_id ?? itemId,
                    kind: doc.kind || "agenda",
                  })),
                ],
              }
            : ag
        )
      );
      setAgendaErrors((prev) => ({ ...prev, [itemId]: "" }));
      setAgendaLastSavedAt(new Date());
      refetchDetail();
    } catch (err) {
      setAgendaErrors((prev) => ({
        ...prev,
        [itemId]: err?.response?.data?.detail || err?.message || "Failed to upload attachment",
      }));
    } finally {
      setAgendaUploading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleDeleteAgendaItem = async (itemId) => {
    if (!canManageAgenda || !itemId) return;
    try {
      await deleteAgendaItem(selectedPack.id, itemId);
      setAgendaItems((prev) => {
        const next = prev.filter((ag) => ag.id !== itemId);
        const ordered = next.map((item, idx) => ({ ...item, order: idx }));
        persistAgendaOrder(ordered);
        return ordered;
      });
      setAgendaLastSavedAt(new Date());
    } catch (err) {
      setAgendaErrors((prev) => ({
        ...prev,
        [itemId]: err?.response?.data?.detail || err?.message || "Failed to delete agenda item",
      }));
    }
  };

  const requestDeleteAgendaItem = (item) => {
    if (!canManageAgenda || !item?.id) return;
    setConfirmAgendaDeleteId({ id: item.id, title: item.title });
  };

  const handleDeletePack = async () => {
    const packId = confirmPackDelete?.id;
    if (!packId || !canDeletePack) return;
    setPackDeleteError("");
    try {
      await deletePack(packId);
      setConfirmPackDelete(null);
      setSelectedId(null);
      await Promise.resolve(refetch());
    } catch (error) {
      const statusCode = error?.response?.status;
      setConfirmPackDelete(null);
      setPackDeleteError(
        statusCode === 409
          ? "This Board Pack can no longer be deleted because it has been published or versioned."
          : error?.response?.data?.detail || "Unable to delete this Board Pack."
      );
    }
  };

  const handleDuplicateAgendaItem = async (item) => {
    if (!canMutateAgenda || !item?.id) return;
    try {
      const payload = buildAgendaPayload(item, 0);
      const created = await createAgendaItem(selectedPack.id, payload);
      const normalized = normalizeAgendaItem(created, 0);
      setAgendaItems((prev) => {
        const next = [normalized, ...prev];
        const ordered = next.map((entry, idx) => ({ ...entry, order: idx }));
        persistAgendaOrder(ordered);
        return ordered;
      });
      setExpandedItemId(normalized.id ?? normalized._localId);
    } catch (err) {
      setAgendaErrors((prev) => ({
        ...prev,
        [item.id]: err?.response?.data?.detail || err?.message || "Failed to duplicate agenda item",
      }));
    }
  };

  const closePackPreview = useCallback(() => {
    setPackPreviewOpen(false);
    setPackPreviewTarget(null);
  }, []);

  const handleOpenPack = async (target = null) => {
    if (!selectedPack) return;
    try {
      await refetchDetail();
    } catch (_err) {
      // Best effort refresh; still open the reader with the latest available pack detail.
    }
    setPackPreviewTarget(target);
    setPackPreviewOpen(true);
  };

  const counts = useMemo(() => {
    const total = packs.length;
    const published = packs.filter((p) => (p.status || "").toLowerCase() === "published").length;
    const draft = packs.filter((p) => (p.status || "").toLowerCase() === "draft").length;
    return { total, published, draft };
  }, [packs]);

  const agendaSavingAny = useMemo(() => Object.values(agendaSaving).some(Boolean), [agendaSaving]);
  const agendaErrorMessage = useMemo(() => {
    const errors = Object.values(agendaErrors).filter(Boolean);
    return errors.length > 0 ? errors[0] : "";
  }, [agendaErrors]);
  const agendaSavedLabel = useMemo(() => {
    if (!agendaLastSavedAt) return "";
    try {
      return `Saved ${agendaLastSavedAt.toLocaleTimeString()}`;
    } catch {
      return "Saved";
    }
  }, [agendaLastSavedAt]);

  const workspaceSections = useMemo(
    () => [
      {
        key: "meeting-packs",
        label: "Meeting packs",
        description: "Review meeting agendas and pack content.",
      },
      {
        key: "calendar",
        label: "Calendar",
        description: "Meeting schedule workspace to be added here.",
      },
      {
        key: "decisions-votes",
        label: "Decisions and votes",
        description: "Decision logs and vote tracking will sit in this view.",
      },
      {
        key: "latest-news",
        label: "Latest news/articles",
        description: "News and article curation will sit in this view.",
      },
    ],
    []
  );

  const selectedAgendaItem = useMemo(() => {
    const current = agendaItems.find((item) => (item.id ?? item._localId) === expandedItemId);
    return current || agendaItems[0] || null;
  }, [agendaItems, expandedItemId]);

  const selectedAgendaItemKey = selectedAgendaItem?.id ?? selectedAgendaItem?._localId ?? null;
  const selectedAgendaIndex = selectedAgendaItem
    ? agendaItems.findIndex((item) => (item.id ?? item._localId) === selectedAgendaItemKey)
    : -1;
  const activeWorkspaceSection = workspaceSections.find((section) => section.key === workspaceSection) || workspaceSections[0];

  const setAgendaItemCollapsed = useCallback((itemKey, collapsed) => {
    if (itemKey === null || itemKey === undefined) return;
    setCollapsedAgendaItems((prev) => {
      const key = String(itemKey);
      if (!collapsed) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  }, []);

  const focusAgendaItem = useCallback(
    (itemKey, options = {}) => {
      if (itemKey === null || itemKey === undefined) return;
      const {
        align = "start",
        openItem = true,
      } = options;

      setExpandedItemId(itemKey);
      if (openItem) {
        setAgendaItemCollapsed(itemKey, false);
      }

      const target = agendaDetailRefs.current.get(itemKey);
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: align, inline: "nearest" });
      }
    },
    [setAgendaItemCollapsed]
  );

  return (
    <div style={pageStyle}>
      <div style={shellCardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Documents
            </div>
            <div style={{ fontWeight: 700, fontSize: 24, color: "#0f172a" }}>
              Board packs and documents
            </div>
            <div style={{ color: "#4b5563", maxWidth: 760 }}>
              Use this workspace to switch between meeting packs, calendar activity, decision tracking, and future board content feeds.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              onClick={() => {
                setCreateError("");
                setCreateOpen(true);
              }}
              variant="primary"
              disabled={!governanceCanEdit}
            >
              + Create Pack
            </Button>
            <Button onClick={() => refetch()} variant="secondary">
              Refresh
            </Button>
          </div>
        </div>

        <div style={workspaceTabRailStyle}>
          {workspaceSections.map((section) => {
            const isActive = workspaceSection === section.key;
            return (
              <Button
                key={section.key}
                onClick={() => setWorkspaceSection(section.key)}
                variant={isActive ? "primary" : "secondary"}
                style={{
                  padding: "10px 14px",
                  borderRadius: 999,
                  fontWeight: 600,
                }}
              >
                {section.label}
              </Button>
            );
          })}
        </div>

        {workspaceSection === "meeting-packs" && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <StatusBadge variant="status" value="published" label={`Published: ${counts.published}`} />
              <StatusBadge variant="status" value="draft" label={`Draft: ${counts.draft}`} />
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Showing {filteredPacks.length} of {packs.length} meetings
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr auto",
                gap: spacing.sm,
                alignItems: "center",
              }}
            >
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                options={[
                  { value: "all", label: "All categories" },
                  ...categoryOptions.map((cat) => ({ value: cat, label: cat })),
                ]}
              />
              <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                options={[
                  { value: "recent", label: "Sort: Newest meeting first" },
                  { value: "oldest", label: "Sort: Oldest meeting first" },
                ]}
              />
              <Button
                onClick={() => {
                  setFilterCategory("all");
                  setFilterDate("");
                  setSortOrder("recent");
                }}
                variant="secondary"
              >
                Reset
              </Button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Meeting packs
              </div>
              <div style={meetingTabRailStyle}>
                {isLoading && <Loader label="Loading meeting packs..." />}
                {!isLoading && filteredPacks.length === 0 && (
                  <div style={{ color: "#6b7280", fontSize: 13 }}>No meeting packs match the current filters.</div>
                )}
                {!isLoading &&
                  meetingTabPacks.map((pack) => {
                    const isSelected = pack.id === selectedId;
                    const meetingDate = getPackMeetingDate(pack);
                    return (
                      <button
                        key={pack.id}
                        ref={(node) => {
                          if (node) {
                            meetingTabRefs.current.set(pack.id, node);
                          } else {
                            meetingTabRefs.current.delete(pack.id);
                          }
                        }}
                        type="button"
                        onClick={() => setSelectedId(pack.id)}
                        style={{
                          border: `1px solid ${isSelected ? "#0f172a" : "#d1d5db"}`,
                          background: isSelected ? "#0f172a" : "#ffffff",
                          color: isSelected ? "#ffffff" : "#111827",
                          borderRadius: 14,
                          padding: "12px 14px",
                          minWidth: 210,
                          textAlign: "left",
                          display: "grid",
                          gap: 4,
                          boxShadow: isSelected ? "0 8px 18px rgba(15,23,42,0.18)" : "none",
                          cursor: "pointer",
                          flex: "0 0 auto",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{pack.title || `Pack #${pack.id}`}</span>
                        <span style={{ fontSize: 12, opacity: 0.82 }}>
                          {meetingDate ? new Date(meetingDate).toLocaleDateString() : "Date not set"}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          </>
        )}
      </div>

      {workspaceSection !== "meeting-packs" ? (
        <div style={placeholderBodyStyle}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {activeWorkspaceSection.label}
          </div>
          <div style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>
            {activeWorkspaceSection.label} workspace
          </div>
          <div style={{ color: "#4b5563", maxWidth: 720 }}>{activeWorkspaceSection.description}</div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            This tab is ready as a top-level section inside Board packs and documents. Its detailed content can be added next.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <div
            role="tablist"
            aria-label="Selected Board Pack sections"
            style={{ display: "flex", gap: 8, flexWrap: "wrap", borderBottom: "1px solid #dbe4ee", paddingBottom: 10 }}
          >
            {[
              ["overview", "Overview"],
              ["agenda", "Agenda"],
              ["documents", "Documents"],
            ].map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={selectedPackTab === tab}
                onClick={() => setSelectedPackTab(tab)}
                style={{
                  border: "1px solid",
                  borderColor: selectedPackTab === tab ? "#0f766e" : "#cbd5e1",
                  borderRadius: 999,
                  padding: "7px 13px",
                  background: selectedPackTab === tab ? "#ecfdf5" : "#ffffff",
                  color: selectedPackTab === tab ? "#115e59" : "#334155",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ ...containerStyle, gridTemplateColumns: "minmax(0, 1.15fr) minmax(340px, 0.85fr)" }}>
          {selectedPackTab === "overview" && (
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Selected board pack
                </div>
                <div aria-label="Selected pack title" style={{ fontWeight: 700, fontSize: 24, color: "#0f172a" }}>
                  {selectedPack?.title || "Select a meeting"}
                </div>
                {selectedPack ? (
                  <div aria-label="Selected pack summary" style={{ display: "grid", gap: 4, marginTop: 6, fontSize: 12, color: "#4b5563" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <StatusBadge
                        variant="status"
                        value={selectedPack.status || "draft"}
                        label={selectedPack.status || "Draft"}
                        style={{ minHeight: 18, padding: "1px 7px", fontSize: 11 }}
                      />
                      {getPackMeetingDate(selectedPack) ? <span>Meeting date: {new Date(getPackMeetingDate(selectedPack)).toLocaleString()}</span> : null}
                      {getPackCategory(selectedPack) ? <span>Committee: {getPackCategory(selectedPack)}</span> : null}
                      {selectedPack.published_version_id ? <span>Published version {selectedPack.published_version_id}</span> : null}
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <span>Documents: {selectedPackDocuments.length}</span>
                      <span>Agenda attachments: {selectedPackAgendaAttachmentCount}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div style={agendaListBodyStyle}>
              {!selectedPack && <EmptyState title="Select a meeting pack to review its agenda" />}
              {selectedPack && (
                <>
                  <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                    Agenda items
                  </div>

                  {detailLoading && <Loader label="Loading agenda..." />}
                  {detailError && !detailLoading && <div style={{ color: "#b91c1c" }}>Failed to load pack details.</div>}

                  {!detailLoading && !detailError && agendaItems.length === 0 && (
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      No agenda items yet. Add the first item to build the agenda.
                    </div>
                  )}

                  {!detailLoading && !detailError && agendaItems.length > 0 && (
                    <div style={{ display: "grid", gap: 8 }} aria-label="Agenda item summaries">
                      {agendaItems.map((item, idx) => {
                        const itemKey = getAgendaItemKey(item, idx);
                        const isSelected = selectedAgendaItemKey === itemKey;
                        const isDragging = String(draggingAgendaKey) === String(itemKey);
                        const isDragTarget = String(dragOverAgendaKey) === String(itemKey) && !isDragging;
                        const docs = Array.isArray(item.documents)
                          ? item.documents
                          : item.document
                            ? [item.document]
                            : [];
                        const votingOn = item.voting?.enabled;
                        return (
                          <button
                            key={itemKey}
                            type="button"
                            draggable={governanceCanEdit}
                            onDragStart={(e) => {
                              if (!governanceCanEdit) return;
                              setDraggingAgendaKey(itemKey);
                              setDragOverAgendaKey(itemKey);
                              e.dataTransfer.effectAllowed = "move";
                              e.dataTransfer.setData("text/plain", itemKey.toString());
                            }}
                            onDragEnter={(e) => {
                              if (!governanceCanEdit) return;
                              e.preventDefault();
                              if (String(draggingAgendaKey) !== String(itemKey)) setDragOverAgendaKey(itemKey);
                            }}
                            onDragOver={(e) => {
                              if (!governanceCanEdit) return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                              if (String(draggingAgendaKey) !== String(itemKey)) setDragOverAgendaKey(itemKey);
                            }}
                            onDragEnd={() => {
                              setDraggingAgendaKey(null);
                              setDragOverAgendaKey(null);
                            }}
                            onDrop={(e) => {
                              if (!governanceCanEdit) return;
                              e.preventDefault();
                              reorderAgendaList(e.dataTransfer.getData("text/plain"), itemKey);
                              setDraggingAgendaKey(null);
                              setDragOverAgendaKey(null);
                            }}
                            onClick={() => {
                              setSelectedPackTab("agenda");
                              focusAgendaItem(itemKey);
                            }}
                            aria-label={`Agenda summary: ${item.title || `Agenda Item ${idx + 1}`}`}
                            aria-grabbed={isDragging}
                            style={{
                              border: `1px solid ${isDragTarget ? "#0f766e" : isSelected ? "#0f172a" : "#e5e7eb"}`,
                              borderRadius: 12,
                              padding: "9px 11px",
                              background: isDragTarget ? "#ecfeff" : isSelected ? "#f8fafc" : "#ffffff",
                              textAlign: "left",
                              display: "grid",
                              gap: 4,
                              cursor: governanceCanEdit ? "grab" : "pointer",
                              opacity: isDragging ? 0.72 : 1,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                              <div style={{ display: "grid", gap: 3 }}>
                                <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Item {idx + 1}</div>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>{item.title || `Agenda Item ${idx + 1}`}</div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  {item.time_alloc ? `${item.time_alloc} min` : "No timing"}
                                </div>
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              {docs.length ? `${docs.length} document${docs.length === 1 ? "" : "s"} attached` : "No documents attached"} | {votingOn ? "Voting enabled" : "Voting off"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )}

          {selectedPackTab === "overview" && (
          <div style={cardStyle}>
            <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Pack details
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>Meeting information</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button onClick={() => handleOpenPack()} disabled={!selectedPack} variant="primary">Open pack</Button>
                <Button onClick={() => setEditing(true)} disabled={!selectedPack || !governanceCanEdit} variant="secondary">Edit pack</Button>
                <details style={{ position: "relative" }}>
                  <summary style={{ cursor: "pointer", color: "#475569", fontSize: 13, fontWeight: 600, padding: "9px 4px" }}>More actions</summary>
                  <div style={{ position: "absolute", right: 0, zIndex: 2, minWidth: 180, display: "grid", gap: 8, padding: 10, border: "1px solid #dbe4ee", borderRadius: 10, background: "#ffffff", boxShadow: "0 12px 30px rgba(15,23,42,0.14)" }}>
                    <Button onClick={() => refetchDetail()} disabled={!selectedPack || detailLoading} variant="secondary">Refresh details</Button>
                    {canDeletePack ? <Button onClick={() => setConfirmPackDelete(selectedPack)} variant="destructive">Delete pack</Button> : null}
                  </div>
                </details>
              </div>
            </div>
            <div style={detailPanelBodyStyle}>
              {packDeleteError ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{packDeleteError}</div> : null}
              {!selectedPack && <EmptyState title="Select a meeting pack to view details" />}
              {selectedPack && (
                <section aria-label="Pack details" style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                    <Detail label="Meeting / category" value={getPackCategory(selectedPack) || "Not set"} />
                    <Detail label="Meeting time" value={formatPackDateTime(getPackMeetingDate(selectedPack))} />
                    <Detail label="Created" value={formatPackDateTime(selectedPack.created_at)} />
                    <Detail label="Last updated" value={formatPackDateTime(selectedPack.updated_at)} />
                    <Detail label="Publication" value={selectedPack.published_version_id ? `Published version ${selectedPack.published_version_id}` : getPackPublishedAt(selectedPack) ? `Published ${formatPackDateTime(getPackPublishedAt(selectedPack))}` : "Draft"} />
                  </div>
                  <div style={{ display: "grid", gap: 4 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Description</div>
                    <div style={{ color: "#334155", lineHeight: 1.45 }}>{selectedPack.description || getPackMetadata(selectedPack).description || "No description provided."}</div>
                  </div>
                  <section aria-label="Quick actions" style={{ display: "grid", gap: 10, marginTop: 8, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>Quick actions</div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>Continue working on this pack</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button onClick={() => setSelectedPackTab("agenda")} disabled={!selectedPack} variant="secondary">Go to Agenda</Button>
                      <Button onClick={() => setSelectedPackTab("documents")} disabled={!selectedPack} variant="secondary">View documents</Button>
                      <Button onClick={() => handleOpenPack({ openQuestions: true })} disabled={!selectedPack} variant="secondary">Questions &amp; responses</Button>
                      <Button onClick={() => { setSelectedPackTab("agenda"); handleAddAgendaItem(); }} disabled={!canMutateAgenda || agendaCreating} variant="secondary">
                        {agendaCreating ? "Adding..." : "Add agenda item"}
                      </Button>
                    </div>
                  </section>
                </section>
              )}
            </div>
          </div>
          )}

          {selectedPackTab === "agenda" && (
          <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
            <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Agenda workspace
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
                  {selectedPack ? `${agendaItems.length} agenda item${agendaItems.length === 1 ? "" : "s"}` : "Select a meeting pack"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  onClick={() => handleOpenPack()}
                  disabled={!selectedPack}
                  variant="primary"
                >
                  Open pack
                </Button>
                <Button onClick={() => refetchDetail()} disabled={!selectedPack || detailLoading} variant="secondary">
                  Refresh details
                </Button>
                <Button onClick={() => setEditing(true)} disabled={!selectedPack || !governanceCanEdit} variant="secondary">
                  Edit pack
                </Button>
                {canDeletePack ? (
                  <Button onClick={() => setConfirmPackDelete(selectedPack)} variant="destructive">
                    Delete pack
                  </Button>
                ) : null}
              </div>
            </div>
            {packDeleteError ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{packDeleteError}</div> : null}

            <div style={detailPanelBodyStyle}>
              {!selectedPack && <EmptyState title="Select a meeting pack to view details" />}
              {selectedPack && agendaItems.length === 0 && (
                <EmptyState title="Add agenda items to build the agenda workspace" />
              )}
              {selectedPack && agendaItems.length > 0 && (
                <div style={{ display: "grid", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, fontSize: 13 }}>
                    <Detail label="Agenda items" value={agendaItems.length} />
                    <Detail label="Selected item" value={selectedAgendaIndex >= 0 ? selectedAgendaIndex + 1 : "-"} />
                    {getPackMeetingDate(selectedPack) && (
                      <Detail label="Meeting Time" value={new Date(getPackMeetingDate(selectedPack)).toLocaleString()} />
                    )}
                    {(selectedPack.owner || getPackMetadata(selectedPack).owner) && (
                      <Detail label="Pack Owner" value={selectedPack.owner || getPackMetadata(selectedPack).owner} />
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 0.3fr) minmax(0, 0.7fr)", gap: 16, alignItems: "start" }}>
                  <aside aria-label="Agenda navigator" style={{ display: "grid", gap: 8, padding: 10, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Agenda navigator</div>
                    {agendaItems.map((agendaItem, agendaIndex) => {
                      const agendaKey = getAgendaItemKey(agendaItem, agendaIndex);
                      const agendaDocs = Array.isArray(agendaItem.documents) ? agendaItem.documents : agendaItem.document ? [agendaItem.document] : [];
                      const active = selectedAgendaItemKey === agendaKey;
                      return <button key={agendaKey} type="button" onClick={() => focusAgendaItem(agendaKey)} style={{ border: `1px solid ${active ? "#0f766e" : "#dbe4ee"}`, borderRadius: 9, padding: "8px 9px", background: active ? "#ecfdf5" : "#ffffff", color: "#0f172a", textAlign: "left", display: "grid", gap: 3, cursor: "pointer" }}>
                        <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>Item {agendaIndex + 1}</span>
                        <span style={{ fontWeight: 700 }}>{agendaItem.title || `Agenda Item ${agendaIndex + 1}`}</span>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{agendaItem.time_alloc ? `${agendaItem.time_alloc} min` : "No timing"} | {agendaDocs.length} document{agendaDocs.length === 1 ? "" : "s"} | {agendaItem.voting?.enabled ? "Voting on" : "Voting off"}</span>
                      </button>;
                    })}
                  </aside>
                  <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Agenda item workspace</div>
                    {agendaSavingAny && <div style={{ fontSize: 12, color: "#0f172a" }}>Saving...</div>}
                    {!agendaSavingAny && agendaSavedLabel && <div style={{ fontSize: 12, color: "#16a34a" }}>{agendaSavedLabel}</div>}
                    {agendaErrorMessage && <div style={{ fontSize: 12, color: "#b91c1c" }}>{agendaErrorMessage}</div>}
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    {agendaItems.filter((item, idx) => getAgendaItemKey(item, idx) === selectedAgendaItemKey).map((item) => {
                      const idx = agendaItems.findIndex((candidate, candidateIndex) => getAgendaItemKey(candidate, candidateIndex) === selectedAgendaItemKey);
                      const itemKey = getAgendaItemKey(item, idx);
                      const itemDocs = Array.isArray(item.documents)
                        ? item.documents
                        : item.document
                          ? [item.document]
                          : [];
                      const itemHasDoc = itemDocs.length > 0;
                      const itemVotingOn = item.voting?.enabled;
                      const isCurrentItem = selectedAgendaItemKey === itemKey;

                      return (
                        <section
                          key={itemKey}
                          ref={(node) => {
                            if (node) {
                              agendaDetailRefs.current.set(itemKey, node);
                            } else {
                              agendaDetailRefs.current.delete(itemKey);
                            }
                          }}
                          style={{
                            border: `1px solid ${isCurrentItem ? "#0f172a" : "#e5e7eb"}`,
                            borderRadius: 12,
                            background: "#ffffff",
                            boxShadow: isCurrentItem ? "0 10px 24px rgba(15,23,42,0.08)" : "none",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              padding: "14px 16px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gap: 4,
                                textAlign: "left",
                                flex: 1,
                              }}
                            >
                              <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Agenda item {idx + 1}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                                {item.title || `Agenda Item ${idx + 1}`}
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {item.time_alloc ? `${item.time_alloc} min` : "No timing"}
                              </div>
                            </div>
                          </div>

                          {(
                            <div style={{ padding: 16, display: "grid", gap: 12 }}>
                              <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
                                <div>
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>Title</label>
                                  <input
                                    value={item.title || ""}
                                    disabled={!governanceCanEdit}
                                    onChange={(e) =>
                                      updateAgendaState(
                                        (prev) =>
                                          prev.map((ag) =>
                                            (ag.id ?? ag._localId) === itemKey
                                              ? { ...ag, title: e.target.value }
                                              : ag
                                          ),
                                        { persistIds: [item.id] }
                                      )
                                    }
                                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>Owner</label>
                                  <input
                                    value={item.owner || ""}
                                    disabled={!governanceCanEdit}
                                    onChange={(e) =>
                                      updateAgendaState(
                                        (prev) =>
                                          prev.map((ag) =>
                                            (ag.id ?? ag._localId) === itemKey
                                              ? { ...ag, owner: e.target.value }
                                              : ag
                                          ),
                                        { persistIds: [item.id] }
                                      )
                                    }
                                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>Time Allocation (mins)</label>
                                  <input
                                    type="number"
                                    value={item.time_alloc || ""}
                                    disabled={!governanceCanEdit}
                                    onChange={(e) =>
                                      updateAgendaState(
                                        (prev) =>
                                          prev.map((ag) =>
                                            (ag.id ?? ag._localId) === itemKey
                                              ? { ...ag, time_alloc: e.target.value }
                                              : ag
                                          ),
                                        { persistIds: [item.id] }
                                      )
                                    }
                                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
                                  />
                                </div>
                              </div>

                              <div>
                                <label style={{ fontSize: 12, color: "#6b7280" }}>Context</label>
                                <textarea
                                  value={item.description || ""}
                                  disabled={!governanceCanEdit}
                                  onChange={(e) =>
                                    updateAgendaState(
                                      (prev) =>
                                        prev.map((ag) =>
                                          (ag.id ?? ag._localId) === itemKey
                                            ? { ...ag, description: e.target.value }
                                            : ag
                                        ),
                                      { persistIds: [item.id] }
                                    )
                                  }
                                  placeholder="Add talking points, links, mentions..."
                                  style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid #d1d5db", minHeight: 90 }}
                                />
                              </div>

                              <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", alignItems: "center" }}>
                                <div
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={(e) => {
                                    if (!governanceCanEdit) return;
                                    e.preventDefault();
                                    const file = e.dataTransfer.files?.[0];
                                    if (!file) return;
                                    handleAgendaUpload(item.id, [file]);
                                  }}
                                >
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>Document</label>
                                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <label
                                      style={{
                                        border: "1px solid #d1d5db",
                                        background: "#fff",
                                        color: "#111827",
                                        padding: "8px 12px",
                                        borderRadius: 8,
                                        cursor: governanceCanEdit ? "pointer" : "not-allowed",
                                        fontWeight: 600,
                                        opacity: governanceCanEdit ? 1 : 0.6,
                                      }}
                                    >
                                      Upload files
                                      <input
                                        type="file"
                                        multiple
                                        disabled={!governanceCanEdit}
                                        onChange={(e) => {
                                          const files = Array.from(e.target.files || []);
                                          if (!files.length) return;
                                          handleAgendaUpload(item.id, files);
                                        }}
                                        style={{ display: "none" }}
                                      />
                                    </label>
                                    {itemHasDoc && (
                                      <span style={{ fontSize: 12, color: "#374151" }}>
                                        {itemDocs.length} file{itemDocs.length === 1 ? "" : "s"}
                                      </span>
                                    )}
                                    {agendaUploading[item.id] && (
                                      <span style={{ fontSize: 12, color: "#0f172a" }}>Uploading...</span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      marginTop: 6,
                                      padding: "10px",
                                      border: "1px dashed #d1d5db",
                                      borderRadius: 8,
                                      color: "#6b7280",
                                      fontSize: 12,
                                    }}
                                  >
                                    Drag & drop to attach/replace document
                                  </div>
                                  {itemHasDoc && (
                                    <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                                      {itemDocs.map((doc, dIdx) => {
                                        const docName = getDocName(doc);
                                        const docType = getDocType(doc);
                                        const docSize = getDocSize(doc);
                                        const ext = docName.split(".").pop()?.toUpperCase();
                                        const thumbSource = doc.preview || doc.url || doc.document_url || doc.file_url || null;
                                        const thumbUrl = thumbSource && isImageDoc(docName, docType) ? thumbSource : null;
                                        return (
                                          <div
                                            key={`${docName}-${dIdx}`}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 10,
                                              border: "1px solid #e5e7eb",
                                              borderRadius: 8,
                                              padding: "6px 8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 8,
                                                overflow: "hidden",
                                                background: "#f1f5f9",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#475569",
                                                fontWeight: 700,
                                                fontSize: 12,
                                              }}
                                            >
                                              {thumbUrl ? (
                                                <img
                                                  src={thumbUrl}
                                                  alt={docName}
                                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                              ) : (
                                                <span>{ext || "FILE"}</span>
                                              )}
                                            </div>
                                            <div style={{ display: "grid", gap: 2 }}>
                                              <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>
                                                {docName}
                                              </div>
                                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                {formatBytes(docSize)}
                                              </div>
                                              <Button
                                                style={{
                                                  border: "1px solid #d1d5db",
                                                  background: "#fff",
                                                  color: "#0f172a",
                                                  padding: "4px 8px",
                                                  borderRadius: 6,
                                                  cursor: "pointer",
                                                  width: "fit-content",
                                                }}
                                                onClick={() =>
                                                  handleOpenPack({
                                                    agendaKey: itemKey,
                                                    attachmentId: doc.agenda_attachment_id || doc.id || null,
                                                    documentId: doc.document_id || doc.id || null,
                                                  })
                                                }
                                              >
                                                Open
                                              </Button>
                                              <Button
                                                style={{
                                                  border: "1px solid #fca5a5",
                                                  background: "#fff1f2",
                                                  color: "#b91c1c",
                                                  padding: "4px 8px",
                                                  borderRadius: 6,
                                                  cursor: canDeleteDocuments ? "pointer" : "not-allowed",
                                                  width: "fit-content",
                                                  opacity: canDeleteDocuments ? 1 : 0.6,
                                                }}
                                                disabled={!canDeleteDocuments}
                                                onClick={() => requestDocDelete(doc, itemKey)}
                                              >
                                                Delete
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <BoardPackItemLinks
                                  linked={item.linked}
                                  user={user}
                                  editable={governanceCanEdit && Boolean(item.id)}
                                  onSave={async (linked) => {
                                    const saved = await updateAgendaItem(selectedPack.id, item.id, { metadata: { linked } });
                                    const metadata = saved.metadata || saved.metadata_json || {};
                                    setAgendaItems((previous) => previous.map((agenda) => agenda.id === item.id
                                      ? { ...agenda, linked: metadata.linked || linked }
                                      : agenda));
                                    setAgendaLastSavedAt(new Date());
                                  }}
                                />
                              </div>

                              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                  <input
                                    type="checkbox"
                                    checked={itemVotingOn || false}
                                    disabled={!governanceCanEdit}
                                    onChange={(e) =>
                                      updateAgendaState(
                                        (prev) =>
                                          prev.map((ag) =>
                                            (ag.id ?? ag._localId) === itemKey
                                              ? { ...ag, voting: { ...(ag.voting || {}), enabled: e.target.checked } }
                                              : ag
                                          ),
                                        { persistIds: [item.id] }
                                      )
                                    }
                                  />
                                  <strong>Enable voting</strong>
                                </div>
                                {itemVotingOn && (
                                  <div style={{ display: "grid", gap: 8 }}>
                                    <input
                                      value={item.voting?.question || ""}
                                      disabled={!governanceCanEdit}
                                      onChange={(e) =>
                                        updateAgendaState(
                                          (prev) =>
                                            prev.map((ag) =>
                                              (ag.id ?? ag._localId) === itemKey
                                                ? { ...ag, voting: { ...(ag.voting || {}), question: e.target.value } }
                                                : ag
                                            ),
                                          { persistIds: [item.id] }
                                        )
                                      }
                                      placeholder='e.g. "Approve budget?"'
                                      style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
                                    />
                                    <div style={{ display: "grid", gap: 6 }}>
                                      {(item.voting?.options || ["Approve", "Approve with conditions", "Reject"]).map((opt, optIdx) => (
                                        <input
                                          key={optIdx}
                                          value={opt}
                                          disabled={!governanceCanEdit}
                                          onChange={(e) =>
                                            updateAgendaState(
                                              (prev) =>
                                                prev.map((ag) => {
                                                  if ((ag.id ?? ag._localId) === itemKey) {
                                                    const opts = [...(ag.voting?.options || [])];
                                                    opts[optIdx] = e.target.value;
                                                    return { ...ag, voting: { ...(ag.voting || {}), options: opts } };
                                                  }
                                                  return ag;
                                                }),
                                              { persistIds: [item.id] }
                                            )
                                          }
                                          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13 }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                <Button
                                  style={{ padding: "6px 10px", borderRadius: 8, cursor: "pointer" }}
                                  onClick={() => requestDeleteAgendaItem(item)}
                                  variant="destructive"
                                  disabled={!canManageAgenda}
                                >
                                  Delete
                                </Button>
                                <Button
                                  style={{ border: "1px solid #d1d5db", background: "#fff", padding: "6px 10px", borderRadius: 8, cursor: "pointer" }}
                                  onClick={() => handleDuplicateAgendaItem(item)}
                                  variant="secondary"
                                  disabled={!canMutateAgenda}
                                >
                                  Duplicate
                                </Button>
                              </div>
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                </div>
                </div>
                </div>
              )}
            </div>
          </div>
          )}

          {selectedPackTab === "documents" && (
            <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
              <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>Documents</div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
                    {selectedPack ? `${selectedPackDocuments.length} document${selectedPackDocuments.length === 1 ? "" : "s"}` : "Select a meeting pack"}
                  </div>
                </div>
                <Button onClick={() => handleOpenPack()} disabled={!selectedPack} variant="primary">Open pack</Button>
              </div>
              <div style={detailPanelBodyStyle}>
                {!selectedPack && <EmptyState title="Select a meeting pack to view its documents" />}
                {selectedPack && selectedPackDocuments.length === 0 && <EmptyState title="No documents are attached to this meeting pack yet" />}
                {selectedPack && selectedPackDocuments.length > 0 && (
                  <div aria-label="Pack documents" style={{ display: "grid", gap: 10 }}>
                    {agendaItems.flatMap((item, itemIndex) => {
                      const itemKey = getAgendaItemKey(item, itemIndex);
                      const itemDocuments = Array.isArray(item.documents) ? item.documents : item.document ? [item.document] : [];
                      return itemDocuments.map((document, documentIndex) => {
                        const documentName = getDocName(document);
                        return (
                          <div key={`${itemKey}-${document.id || document.agenda_attachment_id || documentIndex}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: 12, border: "1px solid #e2e8f0", borderRadius: 10, background: "#ffffff", flexWrap: "wrap" }}>
                            <div style={{ display: "grid", gap: 3 }}>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{documentName}</div>
                              <div style={{ fontSize: 12, color: "#64748b" }}>
                                {item.title || `Agenda item ${itemIndex + 1}`} {getDocType(document) ? ` | ${getDocType(document)}` : ""}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <Button onClick={() => handleOpenPack({ agendaKey: itemKey, attachmentId: document.agenda_attachment_id || document.id, documentId: document.document_id || document.id })} variant="secondary">Open document</Button>
                              <Button onClick={() => window.open(resolvePreviewUrl(`/boardbacks/${selectedPack.id}/agenda/attachments/${document.agenda_attachment_id || document.id}/file`), "_blank", "noopener,noreferrer")} variant="secondary">Download</Button>
                            </div>
                          </div>
                        );
                      });
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      <CreatePackModal
        open={createOpen}
        loading={createLoading}
        error={createError}
        categories={categoryOptions}
        committeeOptions={createCommitteeOptions}
        committeesLoading={createCommitteesLoading}
        noCommitteesMessage={
          isAdminEquivalent
            ? "No committees are available yet. Create one in the admin committee directory before creating a board pack."
            : "You are not assigned to a committee that can own a board pack. Ask an admin to add you to a committee first."
        }
        onClose={() => !createLoading && closeCreateModal()}
        onSave={handleCreatePack}
      />
      <EditModal
        open={editing}
        loading={editLoading}
        error={editError}
        form={editForm}
        categories={categoryOptions}
        selectedIsDraft={selectedIsDraft}
        onClose={() => !editLoading && setEditing(false)}
        onSave={handleSave}
      />
      <DestructiveConfirm
        actionId="BOARDPACK_PACK_DELETE"
        open={Boolean(confirmPackDelete)}
        resourceDisplayName={confirmPackDelete?.title}
        onCancel={() => setConfirmPackDelete(null)}
        onConfirm={handleDeletePack}
      />
      <DestructiveConfirm
        actionId={
          confirmDelete && (confirmDelete.doc?.kind === "agenda" || confirmDelete.doc?.agenda_attachment_id || confirmDelete.doc?.agenda_item_id)
            ? "BOARDPACK_AGENDA_ATTACHMENT_DELETE"
            : "BOARDPACK_DOCUMENT_DELETE"
        }
        open={Boolean(confirmDelete)}
        resourceDisplayName={confirmDelete ? getDocName(confirmDelete.doc) : undefined}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const pending = confirmDelete;
          setConfirmDelete(null);
          if (pending) {
            return handleConfirmDelete(pending);
          }
          return undefined;
        }}
      />
      <DestructiveConfirm
        actionId="BOARDPACK_AGENDA_ITEM_DELETE"
        open={Boolean(confirmAgendaDeleteId)}
        resourceDisplayName={confirmAgendaDeleteId?.title}
        onCancel={() => setConfirmAgendaDeleteId(null)}
        onConfirm={() => {
          const itemId = confirmAgendaDeleteId?.id;
          if (!itemId) return;
          setConfirmAgendaDeleteId(null);
          return handleDeleteAgendaItem(itemId);
        }}
      />
      <MeetingPackModal
        open={packPreviewOpen}
        pack={selectedPack}
        agendaItems={packPreviewAgendaItems}
        initialTarget={packPreviewTarget}
        user={user}
        onClose={closePackPreview}
      />
      <DocumentModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}

// Attach edit modal render to main component
// (Placed at end to keep component definitions nearby)
export function BoardBacksListWithEditor(props) {
  return <BoardBacksList {...props} />;
}

function Detail({ label, value }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span
        style={{
          fontSize: 12,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </span>
      <span style={{ fontWeight: 600, color: "#111827" }}>{value ?? "-"}</span>
    </div>
  );
}

const editSchema = z.object({
  title: z.string().min(1, "Title is required."),
  status: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  owner: z.string().optional().nullable(),
  meeting_time: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z
      .string()
      .nullable()
      .refine((value) => !value || !Number.isNaN(new Date(value).getTime()), {
        message: "Invalid meeting date/time",
      })
  ),
  meeting_location: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
});

const createSchema = z.object({
  title: z.string().trim().min(1, "Meeting name is required."),
  committee_id: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : Number(value)),
    z.number({ invalid_type_error: "Committee is required." }).int().positive("Committee is required.")
  ),
  category: z.string().optional().nullable(),
  meeting_time: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z
      .string()
      .nullable()
      .refine((value) => !value || !Number.isNaN(new Date(value).getTime()), {
        message: "Invalid meeting date/time",
      })
  ),
  meeting_location: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
});

function CreatePackFormFields({
  register,
  formState,
  setValue,
  watch,
  committeeOptions,
  categories,
  committeesLoading,
}) {
  const committeeValue = watch("committee_id");
  const hasCommittees = committeeOptions.length > 0;

  useEffect(() => {
    const firstCommitteeValue = committeeOptions[0]?.value || "";
    const hasCurrentCommittee = committeeOptions.some((option) => option.value === committeeValue);
    if (!hasCommittees) {
      if (committeeValue) {
        setValue("committee_id", "", { shouldValidate: true, shouldDirty: false });
      }
      return;
    }
    if (!committeeValue || !hasCurrentCommittee) {
      setValue("committee_id", firstCommitteeValue, { shouldValidate: true, shouldDirty: false });
    }
  }, [committeeOptions, committeeValue, hasCommittees, setValue]);

  return (
    <div style={{ display: "grid", gap: spacing.md, gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
      <Input
        label="Meeting *"
        error={formState.errors.title?.message}
        placeholder="Quarterly Board Meeting"
        {...register("title")}
      />
      <Select
        label="Committee *"
        error={formState.errors.committee_id?.message}
        options={committeeOptions}
        loading={committeesLoading}
        emptyLabel="No committees available"
        {...register("committee_id")}
      />
      <Select
        label="Tag"
        error={formState.errors.category?.message}
        options={[{ value: "", label: "Tag (optional)" }, ...categories.map((c) => ({ value: c, label: c }))]}
        {...register("category")}
      />
      <Input
        label="Meeting date/time"
        error={formState.errors.meeting_time?.message}
        type="datetime-local"
        {...register("meeting_time")}
      />
      <Input
        label="Location"
        error={formState.errors.meeting_location?.message}
        placeholder="Board Room A"
        {...register("meeting_location")}
      />
      <div style={{ gridColumn: "1 / -1" }}>
        <Textarea
          label="Description"
          error={formState.errors.description?.message}
          style={{ minHeight: 88 }}
          placeholder="Add meeting context or notes"
          {...register("description")}
        />
      </div>
    </div>
  );
}

function CreatePackModal({
  open,
  loading,
  error,
  onClose,
  onSave,
  categories = [],
  committeeOptions = [],
  committeesLoading = false,
  noCommitteesMessage = "",
}) {
  const titleId = "boardpack-create-title";
  const defaultValues = useMemo(
    () => ({
      title: "",
      committee_id: "",
      category: "",
      meeting_time: "",
      meeting_location: "",
      description: "",
    }),
    []
  );
  const hasCommittees = committeeOptions.length > 0;
  if (!open) return null;
  return (
    <ModalA11yWrapper open={open} onClose={() => !loading && onClose()} labelledBy={titleId}>
      <div style={{ padding: spacing.lg, width: "min(640px, 95vw)" }}>
        <h3 id={titleId} style={{ marginTop: 0, marginBottom: spacing.md }}>
          Create Board Pack
        </h3>
        <FormWithValidation
          schema={createSchema}
          defaultValues={defaultValues}
          onSubmit={onSave}
          onCancel={onClose}
          submitLabel={loading ? "Creating..." : "Create pack"}
          submitDisabled={!hasCommittees || committeesLoading}
          cancelDisabled={loading}
          formError={error}
        >
          {({ register, formState, setValue, watch }) => (
            <CreatePackFormFields
              register={register}
              formState={formState}
              setValue={setValue}
              watch={watch}
              committeeOptions={committeeOptions}
              categories={categories}
              committeesLoading={committeesLoading}
            />
          )}
        </FormWithValidation>
        {!committeesLoading && !hasCommittees && !error && noCommitteesMessage && (
          <div role="alert" style={{ color: colors.danger, marginTop: spacing.sm, fontSize: fontSizes.md }}>
            {noCommitteesMessage}
          </div>
        )}
      </div>
    </ModalA11yWrapper>
  );
}

function EditModal({ open, loading, error, form, onClose, onSave, categories = [], selectedIsDraft }) {
  const titleId = "boardpack-edit-title";
  const defaultValues = useMemo(
    () => ({
      title: form?.title || "",
      status: form?.status || "",
      category: form?.category || "",
      owner: form?.owner || "",
      meeting_time: form?.meeting_time || "",
      meeting_location: form?.meeting_location || "",
      description: form?.description || "",
    }),
    [form]
  );
  if (!open) return null;
  return (
    <ModalA11yWrapper open={open} onClose={() => !loading && onClose()} labelledBy={titleId}>
      <div style={{ padding: spacing.lg, width: "min(640px, 95vw)" }}>
        <h3 id={titleId} style={{ marginTop: 0, marginBottom: spacing.md }}>
          Edit Board Pack
        </h3>
        <FormWithValidation
          schema={editSchema}
          defaultValues={defaultValues}
          onSubmit={onSave}
          submitLabel={loading ? "Saving..." : "Save changes"}
          formError={error}
        >
          {({ register, formState }) => (
            <div style={{ display: "grid", gap: spacing.md, gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
              <Input
                label="Title *"
                error={formState.errors.title?.message}
                {...register("title")}
              />
              <Input
                label="Status"
                error={formState.errors.status?.message}
                {...register("status")}
              />
              <Select
                label="Category"
                error={formState.errors.category?.message}
                options={[{ value: "", label: "Category (optional)" }, ...categories.map((c) => ({ value: c, label: c }))]}
                {...register("category")}
              />
              <Input
                label="Owner"
                error={formState.errors.owner?.message}
                {...register("owner")}
              />
              <Input
                label="Meeting time (ISO or YYYY-MM-DD)"
                error={formState.errors.meeting_time?.message}
                {...register("meeting_time")}
              />
              <Input
                label="Meeting location"
                error={formState.errors.meeting_location?.message}
                {...register("meeting_location")}
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <Textarea
                  label="Description"
                  error={formState.errors.description?.message}
                  style={{ minHeight: 80 }}
                  {...register("description")}
                />
              </div>
            </div>
          )}
        </FormWithValidation>
        {!selectedIsDraft && (
          <div style={{ color: colors.danger, marginTop: spacing.sm, fontSize: fontSizes.md }}>
            This pack is not in Draft. Only draft packs can be edited.
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm, marginTop: spacing.md }}>
          <Button onClick={onClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}

function MeetingPackModal({ open, pack, agendaItems = [], initialTarget = null, user = null, onClose }) {
  const titleId = "meeting-pack-preview-title";
  const [expandedPreviewKey, setExpandedPreviewKey] = useState(null);
  const [showOverview, setShowOverview] = useState(false);
  const [questionsPanelOpen, setQuestionsPanelOpen] = useState(false);
  const [displayQuestions, setDisplayQuestions] = useState([]);
  const [questionPageByEntryKey, setQuestionPageByEntryKey] = useState({});
  const docRefs = useRef(new Map());
  const {
    data: loadedQuestions = [],
    isLoading: questionsLoading,
    error: questionsError,
    refetch: refetchQuestions,
  } = useQuery({
    queryKey: ["boardback-questions", pack?.id],
    queryFn: () => listQuestions(pack.id),
    enabled: open && Boolean(pack?.id),
  });

  const readingOrder = useMemo(
    () =>
      agendaItems.flatMap((item, idx) => {
        const docs = Array.isArray(item.documents)
          ? item.documents
          : item.document
            ? [item.document]
            : [];
        const agendaKey = getAgendaItemKey(item, idx);
        return docs.map((doc, docIdx) => {
          const docIdentity = doc?.agenda_attachment_id || doc?.id || doc?.document_id || docIdx;
          const key = `${agendaKey}:${docIdentity}:${docIdx}`;
          return {
            key,
            agendaKey,
            agendaItemId: item?.id ?? null,
            agendaTitle: item.title || `Agenda Item ${idx + 1}`,
            itemIndex: idx,
            docIndex: docIdx,
            doc: { ...doc, pack_id: pack.id },
          };
        });
      }),
    [agendaItems, pack?.id]
  );

  const expandedPreview = useMemo(
    () => readingOrder.find((entry) => entry.key === expandedPreviewKey) || null,
    [expandedPreviewKey, readingOrder]
  );

  useEffect(() => {
    setDisplayQuestions([]);
    setQuestionPageByEntryKey({});
  }, [pack?.id]);

  useEffect(() => {
    if (open) {
      setQuestionsPanelOpen(Boolean(initialTarget?.openQuestions));
    }
  }, [initialTarget?.openQuestions, open, pack?.id]);

  useEffect(() => {
    const incomingQuestions = Array.isArray(loadedQuestions) ? loadedQuestions : [];
    setDisplayQuestions((prev) => {
      if (incomingQuestions.length === 0) {
        return prev;
      }
      const merged = [...incomingQuestions];
      const incomingIds = new Set(incomingQuestions.map((question) => String(question?.id || "")));
      (Array.isArray(prev) ? prev : []).forEach((question) => {
        if (!incomingIds.has(String(question?.id || ""))) {
          merged.push(question);
        }
      });
      return merged;
    });
  }, [loadedQuestions]);

  useEffect(() => {
    if (!open) {
      setExpandedPreviewKey(null);
      setShowOverview(false);
      setQuestionsPanelOpen(false);
      setDisplayQuestions([]);
      setQuestionPageByEntryKey({});
      return;
    }
    if (readingOrder.length === 0) {
      setExpandedPreviewKey(null);
      setShowOverview(true);
      return;
    }

    setShowOverview(false);
    if (!initialTarget) {
      setExpandedPreviewKey(readingOrder[0].key);
      return;
    }

    const matchingEntry =
      readingOrder.find((entry) => {
        const entryAttachmentId = entry.doc?.agenda_attachment_id || entry.doc?.id || null;
        const entryDocumentId = entry.doc?.document_id || entry.doc?.id || null;
        const attachmentMatches = initialTarget.attachmentId
          ? String(entryAttachmentId) === String(initialTarget.attachmentId)
          : true;
        const documentMatches = initialTarget.documentId
          ? String(entryDocumentId) === String(initialTarget.documentId)
          : true;
        const agendaMatches = initialTarget.agendaKey
          ? String(entry.agendaKey) === String(initialTarget.agendaKey)
          : true;
        return attachmentMatches && documentMatches && agendaMatches;
      }) ||
      (initialTarget.agendaKey
        ? readingOrder.find((entry) => String(entry.agendaKey) === String(initialTarget.agendaKey))
        : null) ||
      readingOrder[0];

    setExpandedPreviewKey(matchingEntry?.key || null);
  }, [open, pack?.id, readingOrder, initialTarget]);

  const getEntryAtOffset = useCallback(
    (entryKey, offset) => {
      const currentIndex = readingOrder.findIndex((entry) => entry.key === entryKey);
      if (currentIndex < 0) return null;
      return readingOrder[currentIndex + offset] || null;
    },
    [readingOrder]
  );

  const getNavigationLabel = useCallback((currentEntry, targetEntry, direction) => {
    if (!targetEntry) {
      return direction === "next" ? "No next document" : "No previous document";
    }
    return currentEntry?.agendaKey !== targetEntry.agendaKey
      ? direction === "next"
        ? "Next agenda item"
        : "Previous agenda item"
      : direction === "next"
        ? "Next document"
        : "Previous document";
  }, []);

  const scrollToEntry = useCallback((entryKey) => {
    const node = docRefs.current.get(entryKey);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    if (typeof node.focus === "function") {
      node.focus({ preventScroll: true });
    }
  }, []);

  const activeQuestionEntry = expandedPreview || readingOrder[0] || null;
  const activeQuestionPage = activeQuestionEntry?.key ? questionPageByEntryKey[activeQuestionEntry.key] || null : null;
  const activeQuestionEntryWithPage = activeQuestionEntry
    ? { ...activeQuestionEntry, currentPage: activeQuestionPage }
    : null;
  const sharedQuestions =
    displayQuestions.length > 0
      ? displayQuestions
      : Array.isArray(loadedQuestions)
        ? loadedQuestions
        : [];

  if (!open || !pack) return null;

  return (
    <ModalA11yWrapper
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      backdropStyle={{ background: "rgba(15,23,42,0.52)", zIndex: 3900 }}
      containerStyle={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(96vw, 1180px)",
        height: "min(94vh, 920px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f8fafc", position: "relative" }}>
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid #dbe4ee",
            background: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div
              id={titleId}
              style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}
            >
              {pack.title || "Meeting pack"}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <StatusBadge
                variant="status"
                value={pack.status || "draft"}
                label={pack.status || "Draft"}
                style={{ minHeight: 18, padding: "1px 7px", fontSize: 11 }}
              />
              {getPackCategory(pack) && (
                <StatusBadge
                  variant="status"
                  value={getPackCategory(pack)}
                  label={getPackCategory(pack)}
                  style={{ minHeight: 18, padding: "1px 7px", fontSize: 11 }}
                />
              )}
              {getPackMeetingDate(pack) && (
                <div style={{ fontSize: 12, color: "#475569" }}>
                  {new Date(getPackMeetingDate(pack)).toLocaleString()}
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: "#475569", maxWidth: 840 }}>
              Review the full meeting pack in one window. Each agenda section is separated so it is clear when the pack moves to the next item.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              onClick={() => setQuestionsPanelOpen((prev) => !prev)}
              variant="secondary"
              aria-pressed={questionsPanelOpen}
            >
              Questions
            </Button>
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          <div style={{ flex: 1, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
        {(showOverview || (!expandedPreview && readingOrder.length === 0)) && (
          <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "grid", gap: 18 }}>
          {agendaItems.length === 0 && (
            <EmptyState title="This meeting pack does not have agenda documents yet" />
          )}

          {agendaItems.map((item, idx) => {
            const docs = Array.isArray(item.documents)
              ? item.documents
              : item.document
                ? [item.document]
                : [];
            const itemQuestions = filterQuestionsForAgendaItem(sharedQuestions, item, docs);
            const votingEnabled = Boolean(item.voting?.enabled);

            return (
              <section
                key={getAgendaItemKey(item, idx)}
                style={{
                  border: "1px solid #dbe4ee",
                  borderRadius: 16,
                  overflow: "hidden",
                  background: "#ffffff",
                  boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
                }}
              >
                <div
                  style={{
                    padding: "16px 18px",
                    background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
                    borderBottom: "1px solid #dbe4ee",
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Agenda item {idx + 1}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
                    {item.title || `Agenda Item ${idx + 1}`}
                  </div>
                  {item.description && (
                    <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                      {item.description}
                    </div>
                  )}
                </div>

                <div style={{ padding: 18, display: "grid", gap: 14 }}>
                  {docs.length === 0 && (
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      No documents uploaded for this agenda item.
                    </div>
                  )}

                  {docs.map((doc, docIdx) => {
                    const entry = readingOrder.find(
                      (candidate) =>
                        candidate.itemIndex === idx &&
                        candidate.docIndex === docIdx &&
                        String(candidate.doc?.agenda_attachment_id || candidate.doc?.id || candidate.doc?.document_id || "") ===
                          String(doc?.agenda_attachment_id || doc?.id || doc?.document_id || "")
                    ) || {
                      key: `${getAgendaItemKey(item, idx)}:${docIdx}`,
                      agendaKey: getAgendaItemKey(item, idx),
                      agendaItemId: item?.id ?? null,
                      agendaTitle: item.title || `Agenda Item ${idx + 1}`,
                      itemIndex: idx,
                      docIndex: docIdx,
                      doc: { ...doc, pack_id: pack.id },
                    };
                    const previousEntry = getEntryAtOffset(entry.key, -1);
                    const nextEntry = getEntryAtOffset(entry.key, 1);

                    return (
                    <article
                      key={entry.key}
                      ref={(node) => {
                        if (node) {
                          docRefs.current.set(entry.key, node);
                        } else {
                          docRefs.current.delete(entry.key);
                        }
                      }}
                      tabIndex={-1}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        overflow: "hidden",
                        background: "#f8fafc",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 14px",
                          background: "#ffffff",
                          borderBottom: "1px solid #e5e7eb",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ display: "grid", gap: 3 }}>
                          <div style={{ fontWeight: 600, color: "#0f172a" }}>{getDocName(doc)}</div>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            {getDocType(doc) || "Document"}{getDocSize(doc) ? ` • ${formatBytes(getDocSize(doc))}` : ""}
                          </div>
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setShowOverview(false);
                            setExpandedPreviewKey(entry.key);
                          }}
                        >
                          Maximise
                        </Button>
                      </div>
                      <div style={{ padding: 12 }}>
                        <MeetingPackDocumentPreview
                          doc={entry.doc}
                          questions={itemQuestions.filter((question) => {
                            if (question?.agenda_attachment_id) {
                              return String(question.agenda_attachment_id) === String(entry.doc?.agenda_attachment_id || entry.doc?.id || "");
                            }
                            if (question?.document_id) {
                              return String(question.document_id) === String(entry.doc?.document_id || entry.doc?.id || "");
                            }
                            return String(question?.agenda_item_id || "") === String(entry.agendaItemId || "");
                          })}
                          onCurrentPageChange={(pageNumber) =>
                            setQuestionPageByEntryKey((prev) => ({ ...prev, [entry.key]: pageNumber }))
                          }
                          onQuestionMarkerClick={(pageNumber) => {
                            setQuestionPageByEntryKey((prev) => ({ ...prev, [entry.key]: pageNumber }));
                            setExpandedPreviewKey(entry.key);
                            setShowOverview(false);
                            setQuestionsPanelOpen(true);
                          }}
                          footerActions={[
                            {
                              key: "maximise",
                              label: "Maximise",
                              onClick: () => {
                                setShowOverview(false);
                                setExpandedPreviewKey(entry.key);
                              },
                            },
                            {
                              key: "previous",
                              label: getNavigationLabel(entry, previousEntry, "previous"),
                              disabled: !previousEntry,
                              onClick: () => previousEntry && scrollToEntry(previousEntry.key),
                            },
                            {
                              key: "next",
                              label: getNavigationLabel(entry, nextEntry, "next"),
                              disabled: !nextEntry,
                              onClick: () => nextEntry && scrollToEntry(nextEntry.key),
                            },
                          ]}
                        />
                      </div>
                    </article>
                  );
                  })}

                  {(votingEnabled || itemQuestions.length > 0) && (
                    <section
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        padding: 14,
                        background: "#f8fafc",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Vote and questions</div>
                        {votingEnabled ? (
                          <span style={{ fontSize: 12, color: "#0f766e", fontWeight: 600 }}>Voting enabled</span>
                        ) : (
                          <span style={{ fontSize: 12, color: "#64748b" }}>Questions only</span>
                        )}
                      </div>
                      {votingEnabled && (
                        <div style={{ display: "grid", gap: 4 }}>
                          <div style={{ fontSize: 13, color: "#334155" }}>
                            {item.voting?.question || "No vote prompt configured for this agenda item yet."}
                          </div>
                          {Array.isArray(item.voting?.options) && item.voting.options.length > 0 && (
                            <div style={{ fontSize: 12, color: "#64748b" }}>
                              Options: {item.voting.options.join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                      {itemQuestions.length === 0 ? (
                        <div style={{ fontSize: 12, color: "#64748b" }}>No questions raised for this agenda item yet.</div>
                      ) : (
                        <div style={{ display: "grid", gap: 8 }}>
                          {itemQuestions.map((question) => (
                            <button
                              key={question.id}
                              type="button"
                              onClick={() => {
                                const matchingEntry = readingOrder.find((entry) => {
                                  if (question?.agenda_attachment_id) {
                                    return String(entry.doc?.agenda_attachment_id || entry.doc?.id || "") === String(question.agenda_attachment_id);
                                  }
                                  if (question?.document_id) {
                                    return String(entry.doc?.document_id || entry.doc?.id || "") === String(question.document_id);
                                  }
                                  return String(entry.agendaItemId || "") === String(question?.agenda_item_id || "");
                                });
                                if (matchingEntry) {
                                  setQuestionPageByEntryKey((prev) => ({
                                    ...prev,
                                    [matchingEntry.key]: question?.page_number || prev[matchingEntry.key] || 1,
                                  }));
                                  setExpandedPreviewKey(matchingEntry.key);
                                  setShowOverview(false);
                                }
                                setQuestionsPanelOpen(true);
                              }}
                              style={{
                                border: "1px solid #dbe4ee",
                                borderRadius: 12,
                                padding: 10,
                                background: "#ffffff",
                                textAlign: "left",
                                display: "grid",
                                gap: 4,
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                <div style={{ fontWeight: 600, color: "#0f172a" }}>{question.title}</div>
                                <div style={{ fontSize: 11, color: "#64748b" }}>
                                  {question.page_number ? `Page ${question.page_number}` : "Agenda-level"}
                                </div>
                              </div>
                              <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{question.body}</div>
                              <div style={{ fontSize: 11, color: "#64748b" }}>
                                {getQuestionActorName(question)} · {question.created_at ? new Date(question.created_at).toLocaleString() : "Just now"}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </section>
            );
          })}
          </div>
        )}

        {!showOverview && expandedPreview && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(248,250,252,0.98)",
              display: "flex",
              flexDirection: "column",
              zIndex: 2,
            }}
          >
            <div
              style={{
                padding: "18px 22px",
                borderBottom: "1px solid #dbe4ee",
                background: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
                  {getDocName(expandedPreview.doc)}
                </div>
                <div style={{ fontSize: 12, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {expandedPreview.agendaTitle}
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  Use the scrollable document pane below to review the full attachment.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setExpandedPreviewKey(null);
                    setShowOverview(true);
                  }}
                >
                  Back to pack
                </Button>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: 20 }}>
              <MeetingPackDocumentPreview
                key={expandedPreview.key}
                doc={expandedPreview.doc}
                frameHeight="100%"
                maximised
                questions={sharedQuestions.filter((question) => {
                  if (question?.agenda_attachment_id) {
                    return String(question.agenda_attachment_id) === String(expandedPreview.doc?.agenda_attachment_id || expandedPreview.doc?.id || "");
                  }
                  if (question?.document_id) {
                    return String(question.document_id) === String(expandedPreview.doc?.document_id || expandedPreview.doc?.id || "");
                  }
                  return String(question?.agenda_item_id || "") === String(expandedPreview.agendaItemId || "");
                })}
                onCurrentPageChange={(pageNumber) =>
                  setQuestionPageByEntryKey((prev) => ({ ...prev, [expandedPreview.key]: pageNumber }))
                }
                onQuestionMarkerClick={(pageNumber) => {
                  setQuestionPageByEntryKey((prev) => ({ ...prev, [expandedPreview.key]: pageNumber }));
                  setQuestionsPanelOpen(true);
                }}
                footerActions={[
                  {
                    key: "back",
                    label: "Back to pack",
                    onClick: () => {
                      setExpandedPreviewKey(null);
                      setShowOverview(true);
                    },
                  },
                  {
                    key: "previous",
                    label: getNavigationLabel(expandedPreview, getEntryAtOffset(expandedPreview.key, -1), "previous"),
                    disabled: !getEntryAtOffset(expandedPreview.key, -1),
                    onClick: () => {
                      const previousEntry = getEntryAtOffset(expandedPreview.key, -1);
                      if (previousEntry) setExpandedPreviewKey(previousEntry.key);
                    },
                  },
                  {
                    key: "next",
                    label: getNavigationLabel(expandedPreview, getEntryAtOffset(expandedPreview.key, 1), "next"),
                    disabled: !getEntryAtOffset(expandedPreview.key, 1),
                    onClick: () => {
                      const nextEntry = getEntryAtOffset(expandedPreview.key, 1);
                      if (nextEntry) setExpandedPreviewKey(nextEntry.key);
                    },
                  },
                ]}
              />
            </div>
          </div>
        )}
          </div>
          {questionsPanelOpen && (
            <BoardPackQuestionsPanel
              pack={pack}
              currentEntry={activeQuestionEntryWithPage}
              currentUser={user}
              canParticipate
              questions={sharedQuestions}
              questionsLoading={questionsLoading}
              questionsError={questionsError}
              onQuestionsChange={setDisplayQuestions}
              onRefetchQuestions={refetchQuestions}
              open={questionsPanelOpen}
              onClose={() => setQuestionsPanelOpen(false)}
            />
          )}
        </div>
      </div>
    </ModalA11yWrapper>
  );
}

export function MeetingPackDocumentPreview({
  doc,
  frameHeight = "clamp(420px, 68vh, 960px)",
  maximised = false,
  questions = [],
  onCurrentPageChange,
  onQuestionMarkerClick,
  footerActions = [],
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const safeDoc = doc || {};
  const docName = getDocName(safeDoc);
  const docType = getDocType(safeDoc);
  const isPdf = isPdfDoc(docName, docType);
  const isImage = isImageDoc(docName, docType);
  const docId = safeDoc.id || safeDoc.document_id;
  const isAgendaAttachment =
    safeDoc?.kind === "agenda" || safeDoc?.agenda_attachment_id || safeDoc?.agenda_item_id;
  const packId = safeDoc.pack_id || safeDoc.packId;
  const attachmentId = isAgendaAttachment ? safeDoc.agenda_attachment_id || docId : null;
  const stableFileUrl = isAgendaAttachment
    ? packId && attachmentId
      ? `/boardbacks/${packId}/agenda/attachments/${attachmentId}/file`
      : null
    : docId
      ? `/boardbacks/documents/${docId}/file`
      : null;
  const inlineFileUrl = stableFileUrl
    ? `${stableFileUrl}${stableFileUrl.includes("?") ? "&" : "?"}disposition=inline`
    : null;
  const resolvedInlineFileUrl = inlineFileUrl ? resolvePreviewUrl(inlineFileUrl) : null;
  const downloadUrl = stableFileUrl ? resolvePreviewUrl(stableFileUrl) : null;
  const previewActions = [
    ...(downloadUrl
      ? [
          {
            key: "download",
            label: "Download",
            onClick: () => window.open(downloadUrl, "_blank", "noopener,noreferrer"),
          },
        ]
      : []),
    ...footerActions,
  ];
  const hasFooterActions = previewActions.length > 0;

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setPreviewError("");
    setPreviewUrl(null);

    if (isPdf) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    const existingUrl = safeDoc.preview || safeDoc.url || safeDoc.document_url || safeDoc.file_url || null;
    const isBlobLike = existingUrl && (existingUrl.startsWith("blob:") || existingUrl.startsWith("data:"));
    if (isBlobLike) {
      setPreviewUrl(existingUrl);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    const resolvedExisting = existingUrl ? resolvePreviewUrl(existingUrl) : null;
    const fetchUrl = resolvedInlineFileUrl || resolvedExisting;

    if (!fetchUrl) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    fetchDocument(fetchUrl)
      .then(async (response) => {
        if (!active) return;
        if (!response.ok) {
          throw new Error(await readPreviewFailure(response, "Preview not available in the pack viewer for this document."));
        }
        const contentType = response.headers.get("content-type") || "";
        if (
          contentType.includes("application/json") ||
          contentType.includes("text/html") ||
          contentType.includes("text/plain")
        ) {
          throw new Error("Preview not available in the pack viewer for this document.");
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((error) => {
        if (active) {
          setPreviewError(error?.message || "Preview not available in the pack viewer for this document.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isPdf, resolvedInlineFileUrl, safeDoc.preview, safeDoc.url, safeDoc.document_url, safeDoc.file_url]);

  const renderFallback = (message) => (
    <div style={{ padding: 16, color: "#64748b", display: "grid", gap: 10 }}>
      <div>{message}</div>
      {stableFileUrl && (
        <a
          href={resolvedInlineFileUrl || resolvePreviewUrl(stableFileUrl)}
          target="_blank"
          rel="noreferrer"
          style={{ color: "#0f5d56", fontWeight: 600, textDecoration: "none" }}
        >
          Open document in a new tab
        </a>
      )}
    </div>
  );

  const renderFooterBar = () =>
    hasFooterActions ? (
      <div
        data-testid="meeting-pack-preview-footer"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "14px 16px",
          borderTop: "1px solid #dbe4ee",
          background: "rgba(255,255,255,0.96)",
        }}
      >
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Continue reviewing the pack without leaving this window.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {previewActions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant || "secondary"}
              onClick={action.onClick}
              disabled={Boolean(action.disabled)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    ) : null;

  const renderPreviewShell = (content, { contentTestId = undefined, centerContent = false } = {}) => (
    <div
      style={{
        borderRadius: 12,
        background: "#e2e8f0",
        height: frameHeight,
        minHeight: maximised ? 0 : 420,
        border: "1px solid #dbe4ee",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        data-testid={contentTestId}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable both-edges",
          padding: 16,
          display: "grid",
          gap: 16,
          alignContent: "start",
          justifyItems: centerContent ? "center" : "stretch",
        }}
      >
        {content}
      </div>
      {renderFooterBar()}
    </div>
  );

  if (loading) {
    return renderPreviewShell(<div style={{ color: "#64748b" }}>Loading preview...</div>);
  }

  if (previewError) {
    return renderPreviewShell(renderFallback(previewError));
  }

  if (isPdf && stableFileUrl) {
    return renderPreviewShell(
      <BoardDocumentViewer
        documentId={isAgendaAttachment ? null : docId}
        attachmentId={isAgendaAttachment ? attachmentId : null}
        fileUrl={stableFileUrl}
        showNotesPanel={false}
        questions={questions}
        onCurrentPageChange={onCurrentPageChange}
        onQuestionMarkerClick={onQuestionMarkerClick}
      />,
      { contentTestId: "meeting-pack-pdf-scroll-frame" }
    );
  }

  if (isImage && previewUrl) {
    return renderPreviewShell(
      <img
        src={previewUrl}
        alt={docName}
        style={{ width: "100%", height: "auto", maxHeight: "100%", objectFit: "contain", background: "#ffffff" }}
      />,
      { centerContent: true }
    );
  }

  return renderPreviewShell(renderFallback("Preview not available in the pack viewer for this document type."));
}

function DocumentModal({ doc, onClose }) {
  const [drag, setDrag] = useState({ active: false, offsetX: 0, offsetY: 0, x: 0, y: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const safeDoc = doc || {};
  const docName = getDocName(safeDoc);
  const docType = getDocType(safeDoc);
  const isPdf = isPdfDoc(docName, docType);
  const isImage = isImageDoc(docName, docType);
  const docId = safeDoc.id || safeDoc.document_id;
  const isAgendaAttachment =
    safeDoc?.kind === "agenda" || safeDoc?.agenda_attachment_id || safeDoc?.agenda_item_id;
  const packId = safeDoc.pack_id || safeDoc.packId;
  const attachmentId = isAgendaAttachment ? docId : null;
  const canAnnotate =
    isPdf && ((isAgendaAttachment && attachmentId && packId) || (!isAgendaAttachment && docId));

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setPreviewError("");
    setPreviewUrl(null);
    if (!doc) {
      setPreviewUrl(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    if (canAnnotate) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    const existingUrl = doc.preview || doc.url || doc.document_url || doc.file_url || null;
    const isBlobLike = existingUrl && (existingUrl.startsWith("blob:") || existingUrl.startsWith("data:"));
    if (isBlobLike) {
      setPreviewUrl(existingUrl);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    const base = apiClient.defaults.baseURL || "";
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const absoluteBase = normalizedBase.startsWith("http")
      ? normalizedBase
      : `${window.location.origin}${normalizedBase}`;
    const resolvedExisting = existingUrl ? resolvePreviewUrl(existingUrl) : null;
    const attachmentFileUrl =
      packId && attachmentId
        ? `${absoluteBase}/boardbacks/${packId}/agenda/attachments/${attachmentId}/file`
        : null;
    const fallbackUrl = isAgendaAttachment
      ? attachmentFileUrl || resolvedExisting
      : docId
        ? `${absoluteBase}/boardbacks/documents/${docId}/file`
        : resolvedExisting;
    const fetchUrl = resolvedExisting && isTrustedDocumentUrl(resolvedExisting)
      ? resolvedExisting
      : fallbackUrl;

    if (!fetchUrl) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    const fetchBlobFromUrl = async (url) => {
      const response = await fetchDocument(url);
      if (!response.ok) {
        throw new Error(await readPreviewFailure(response));
      }
      const contentType = response.headers.get("content-type") || "";
      if (
        contentType.includes("application/json") ||
        contentType.includes("text/html") ||
        contentType.includes("text/plain")
      ) {
        throw new Error("Preview not available. Download or open in your viewer.");
      }
      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);
      return objectUrl;
    };

    fetchDocument(fetchUrl)
      .then(async (response) => {
        if (!active) return;
        if (!response.ok) {
          throw new Error(await readPreviewFailure(response));
        }
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("text/html") || contentType.includes("text/plain")) {
          setPreviewError("Preview not available. Download or open in your viewer.");
          return;
        }
        if (contentType.includes("application/json")) {
          const text = await response.text();
          try {
            const parsed = JSON.parse(text);
            if (parsed?.url && isTrustedDocumentUrl(fetchUrl)) {
              try {
                const resolved = resolvePreviewUrl(parsed.url);
                const blobUrl = await fetchBlobFromUrl(resolved);
                setPreviewUrl(blobUrl);
              } catch {
                setPreviewError("Preview not available. Download or open in your viewer.");
              }
            } else {
              setPreviewError("Preview not available. Download or open in your viewer.");
            }
          } catch {
            setPreviewError("Preview not available. Download or open in your viewer.");
          }
          return;
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((error) => {
        if (active) {
          setPreviewError(error?.message || "Preview not available. Download or open in your viewer.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [doc, docId, isPdf, canAnnotate, attachmentId, packId, isAgendaAttachment]);

  const titleId = "boardpack-doc-preview-title";

  const startDrag = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDrag({
      active: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      x: rect.left,
      y: rect.top,
    });
  };

  useEffect(() => {
    if (!drag.active) return undefined;
    const handleMove = (event) => {
      setDrag((d) =>
        d.active ? { ...d, x: event.clientX - d.offsetX, y: event.clientY - d.offsetY } : d
      );
    };
    const handleUp = () => setDrag((d) => ({ ...d, active: false }));
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [drag.active]);

  if (!doc) return null;

  return (
    <ModalA11yWrapper
      open={Boolean(doc)}
      onClose={onClose}
      labelledBy={titleId}
      backdropStyle={{ background: "rgba(15,23,42,0.35)", zIndex: 4000 }}
      containerStyle={{
        position: "absolute",
        left: drag.x || "50%",
        top: drag.y || "50%",
        transform: drag.x ? "none" : "translate(-50%, -50%)",
        width: "min(90vw, 900px)",
        height: "min(90vh, 700px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          style={{
            padding: `${spacing.md}px ${spacing.lg}px`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "move",
            borderBottom: "1px solid rgba(107, 114, 128, 0.2)",
            userSelect: "none",
          }}
          onMouseDown={startDrag}
        >
          <div id={titleId} style={{ fontWeight: 700, color: colors.text }}>
            {docName}
          </div>
          <Button onClick={onClose} variant="ghost">
            X
          </Button>
        </div>
        <div style={{ flex: 1, background: "rgba(107, 114, 128, 0.08)", overflow: "auto" }}>
          {canAnnotate ? (
            <BoardDocumentViewer
              documentId={isAgendaAttachment ? null : docId}
              attachmentId={isAgendaAttachment ? attachmentId : null}
              fileUrl={
                isAgendaAttachment
                  ? `/boardbacks/${packId}/agenda/attachments/${attachmentId}/file`
                  : `/boardbacks/documents/${docId}/file`
              }
            />
          ) : (
            <>
              {loading && <div style={{ padding: spacing.lg, color: colors.secondary }}>Loading preview...</div>}
              {!loading && previewError && (
                <div style={{ padding: spacing.lg, color: colors.secondary }}>{previewError}</div>
              )}
              {!loading && !previewError && isPdf && previewUrl && (
                <iframe title={docName} src={previewUrl} style={{ width: "100%", height: "100%", border: "none" }} />
              )}
              {!loading && !previewError && isImage && previewUrl && (
                <img
                  src={previewUrl}
                  alt={docName}
                  onError={() => setPreviewError("Preview not available. Download or open in your viewer.")}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
              {!loading && !previewError && !previewUrl && (
                <div style={{ padding: spacing.lg, color: colors.secondary }}>
                  Preview not available. Download or open in your viewer.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ModalA11yWrapper>
  );
}
